// ================= ProductOptionsComponent (tabs + headings + scroll [no fetch]) =================
// Fuctions:
// - Tabs: "All" shows all collections all panels open A single collection shows only its panel.
// - Headings always visible: clicking a heading also opens that collection same as clicking the menu
// - Initial selection from URL param (?collection_handle=) if not param all open.

function ensureAbsoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("//")) return window.location.protocol + url;
  return url;
}

function extractImageAttrsFromElement(imgEl) {
  if (!imgEl) return null;
  const rawSrc =
    imgEl.currentSrc ||
    imgEl.getAttribute("src") ||
    imgEl.dataset.src ||
    imgEl.dataset.lazySrc ||
    "";
  const src = ensureAbsoluteUrl(rawSrc);
  const srcset = imgEl.getAttribute("srcset") || imgEl.dataset.srcset || "";
  const sizes = imgEl.getAttribute("sizes") || "";
  const alt = imgEl.getAttribute("alt") || "";

  const widthAttr = parseInt(imgEl.getAttribute("width") || "0", 10);
  const heightAttr = parseInt(imgEl.getAttribute("height") || "0", 10);
  const width = widthAttr > 0 ? widthAttr : imgEl.naturalWidth || 0;
  const height = heightAttr > 0 ? heightAttr : imgEl.naturalHeight || 0;

  return { src, alt, srcset, sizes, width, height };
}

function addVariantToPack({
  variantId,
  productId = "",
  collectionId = "314985971899",
  productTitle = "",
  scentName = "",
  price = 0,
  sku = "",
  vendor = "",
  productType = "",
  imageEl = null,
  qty = 1,
  edition = "",
}) {
  // Normalize image attributes exactly like cards do
  const imageAttrs = extractImageAttrsFromElement(imageEl);

  // Guard: do not proceed without an image (PackPreview relies on imageAttrs)
  if (!imageAttrs) {
    console.error("[BYP] Missing/invalid image for variant:", variantId);
    return;
  }

  // Push N times (the PackState model uses "one element == one unit")
  for (let i = 0; i < qty; i++) {
    packState.addItem({
      id: String(variantId),
      productId: String(productId),
      collectionId: String(collectionId),
      title: scentName || productTitle,
      productTitle: String(productTitle),
      scentName: String(scentName),
      price: Number(price) || 0,
      sku: String(sku),
      vendor: String(vendor),
      productType: String(productType),
      imageAttrs,
      quantity: 1,
      edition: String(edition),
    });
  }
}

class ProductOptionsComponent extends HTMLElement {
  constructor() {
    super();
    this.activeHandle = "all";
    // Bindings
    this.onMenuClick = this.onMenuClick.bind(this);
    this.onHeadingClick = this.onHeadingClick.bind(this);
    this.onHeadingKey = this.onHeadingKey.bind(this);
    this.calculateInitialOffsets = this.calculateInitialOffsets.bind(this);
    this.onScrollSticky = this.onScrollSticky.bind(this);
  }

  connectedCallback() {
    // --- DOM refs ---
    this.$menu = document.getElementById("collection-menu");
    this.$menuLinks = this.$menu
      ? Array.from(
          this.$menu.querySelectorAll(".product-pack__navigation-link")
        )
      : [];
    // Look only inside this component for headings/panels
    this.$headings = Array.from(
      this.querySelectorAll(
        ".product-pack-options__heading[data-collection-handle]"
      )
    );
    this.$panels = Array.from(
      this.querySelectorAll(
        ".product-pack-options__wrapper[data-collection-handle]"
      )
    );
    this.$loadMoreWraps = Array.from(
      this.querySelectorAll(
        ".product-pack-options__loadmore[data-collection-handle]"
      )
    );

    // --- Wire events (menu + headings) ---
    if (this.$menu) this.$menu.addEventListener("click", this.onMenuClick);
    this.$headings.forEach((h) => {
      h.addEventListener("click", this.onHeadingClick);
      h.addEventListener("keydown", this.onHeadingKey);
      // Make headings keyboard-focusable and button-like if not already
      if (!h.hasAttribute("tabindex")) h.setAttribute("tabindex", "0");
      if (!h.hasAttribute("role")) h.setAttribute("role", "button");
    });

    // --- Initial selection: URL param > active link > 'all' ---
    const urlHandle = this.getCollectionHandleFromURL();
    const handlesInMenu = this.$menuLinks.map(
      (a) => a.dataset.collectionHandle
    );
    this.activeHandle =
      urlHandle && handlesInMenu.includes(urlHandle)
        ? urlHandle
        : this.$menuLinks.find((a) =>
            a.classList.contains("product-pack__navigation-link--active")
          )?.dataset.collectionHandle || "all";

    this.applySelection(this.activeHandle, { scroll: true });

    // --- Keep your existing behaviors ---
    this.initializeModalFunctionality();
    this.initSticky();
  }

  // ========== Menu / Heading interactions ==========
  onMenuClick(e) {
    const a = e.target.closest(".product-pack__navigation-link");
    if (!a) return;
    e.preventDefault();

    const handle = a.dataset.collectionHandle;
    if (!handle || handle === this.activeHandle) return;

    this.applySelection(handle, { scroll: true });
  }

  onHeadingClick(e) {
    const interactive = e.target?.closest(
      [
        ".product-pack-filters",
        "a",
        "input",
        "select",
        "textarea",
        "[data-action]",
      ].join(",")
    );
    if (interactive) return;

    const heading = e.currentTarget;
    const handle = heading?.dataset?.collectionHandle;
    if (!handle) return;

    // If the user clicks the currently "selected" handle, toggle accordion open/close.
    if (handle === this.activeHandle) {
      this.toggleAccordionPanel(handle);
      document.dispatchEvent(new CustomEvent("byp:layout:changed"));
      return;
    }

    // If it's a different handle, keep existing behavior (switch selection),
    // but ensure the panel ends up expanded.
    this.applySelection(handle, { scroll: false });
    this.setPanelExpanded(handle, true);
    document.dispatchEvent(new CustomEvent("byp:layout:changed"));
  }

  onHeadingKey(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.onHeadingClick(e);
    }
  }

  toggleAccordionPanel(handle) {
    const heading = this.$headings.find((h) => h.dataset.collectionHandle === handle);
    const panel = this.$panels.find((p) => p.dataset.collectionHandle === handle);
    const loadMore = this.$loadMoreWraps.find((w) => w.dataset.collectionHandle === handle);

    if (!heading || !panel) return;

    const isExpanded = heading.getAttribute("aria-expanded") === "true";
    const nextExpanded = !isExpanded;

    this.setPanelExpanded(handle, nextExpanded);
  }

  setPanelExpanded(handle, expanded) {
  const heading = this.$headings.find((h) => h.dataset.collectionHandle === handle);
  const panel = this.$panels.find((p) => p.dataset.collectionHandle === handle);
  const loadMore = this.$loadMoreWraps.find((w) => w.dataset.collectionHandle === handle);

  if (!heading || !panel) return;

  heading.setAttribute("aria-expanded", String(!!expanded));
  panel.toggleAttribute("hidden", !expanded);

  // Keep Load More aligned with the panel state
  if (loadMore) loadMore.toggleAttribute("hidden", !expanded);
}

  // ========== Core selection logic ==========
  applySelection(handle, { scroll } = { scroll: false }) {
    this.activeHandle = handle;
    this.setActiveLink(handle);

    if (handle === "all") {
      this.openAllPanels();
      if (scroll) this.scrollToFirstHeading();
    } else {
      this.openOnlyPanel(handle);
      if (scroll) this.scrollToHeading(handle);
    }

    // Let Load More / layout recompute (columns/visibility may change)
    document.dispatchEvent(new CustomEvent("byp:layout:changed"));
  }

  setActiveLink(handle) {
    this.$menuLinks.forEach((a) => {
      const isActive =
        (handle === "all" && a.dataset.collectionHandle === "all") ||
        a.dataset.collectionHandle === handle;
      a.classList.toggle("product-pack__navigation-link--active", isActive);
      a.setAttribute("aria-selected", String(isActive));
    });
  }

  openAllPanels() {
    // Headings always visible; mark 'expanded'
    this.$headings.forEach((h) => h.setAttribute("aria-expanded", "true"));
    // Show all panels + load-more containers
    this.$panels.forEach((p) => p.removeAttribute("hidden"));
    this.$loadMoreWraps.forEach((w) => w.removeAttribute("hidden"));
  }

  openOnlyPanel(handle) {
    // Headings: visible always; set aria-expanded per match
    this.$headings.forEach((h) =>
      h.setAttribute(
        "aria-expanded",
        String(h.dataset.collectionHandle === handle)
      )
    );
    // Panels: show only the selected one
    this.$panels.forEach((p) =>
      p.toggleAttribute("hidden", p.dataset.collectionHandle !== handle)
    );
    // Load more buttons: show only for the active panel
    this.$loadMoreWraps.forEach((w) =>
      w.toggleAttribute("hidden", w.dataset.collectionHandle !== handle)
    );
  }

  // ========== Scrolling helpers ==========
  scrollToHeading(handle) {
    const h =
      this.$headings.find((x) => x.dataset.collectionHandle === handle) ||
      this.$headings[0];
    if (!h) return;
    const headerOffset =
      parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-desktop-sticky-height")
          .replace(/px/gi, "")
      ) || 0;
    const y =
      h.getBoundingClientRect().top +
      window.scrollY -
      Math.max(headerOffset, 0) -
      55;
    window.scrollTo({ top: y, behavior: "smooth" });
  }

  scrollToFirstHeading() {
    if (this.$headings.length === 0) return;
    this.scrollToHeading(this.$headings[0].dataset.collectionHandle);
  }

  getCollectionHandleFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("collection_handle");
  }

  initializeModalFunctionality() {
    // SubCategory modal and scroll functionality (unchanged)
    const modalTriggers = document.querySelectorAll(
      ".subcollections-nav-modal-trigger"
    );
    modalTriggers.forEach((modalTrigger) => {
      const modal = document.getElementById("modal-pack-subcategories");

      if (modalTrigger) {
        modalTrigger.addEventListener("click", () => {
          window.emit("modal:open", null, { modalContent: modal });
          const modalContainer = document.querySelector(".modal.active");

          const subcategoryButtons = modalContainer.querySelectorAll(
            ".subcategory-button"
          );
          subcategoryButtons.forEach((button) => {
            button.addEventListener("click", (event) => {
              const targetId = event.target.getAttribute("data-scroll-target");
              const targetElement = document.getElementById(targetId);

              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
                const closeButton =
                  modalContainer.querySelector("[data-modal-close]");
                closeButton.click();
              }
            });
          });
        });
      }
    });
  }

  // ===== Sticky logic (navigation always sticky; items sticky only on mobile) =====
  initSticky() {
    // Cache elements
    this.navigationElement = document.querySelector(
      ".product-pack__navigation"
    );
    this.itemsElement = document.querySelector(".product-pack__items");

    // Guard
    if (!this.navigationElement) return;

    // Bind once
    this.calculateInitialOffsets = this.calculateInitialOffsets.bind(this);
    this.onScrollSticky = this.onScrollSticky.bind(this);

    // Vars
    this.headerHeight = 0;
    this.navigationPlaceholder = null;
    this.itemsPlaceholder = null;

    // Compute initial offsets
    this.calculateInitialOffsets();

    // Recompute on resize and on layout changes (load more, filters, etc.)
    window.addEventListener("resize", this.calculateInitialOffsets, {
      passive: true,
    });
    document.addEventListener(
      "byp:layout:changed",
      this.calculateInitialOffsets
    );

    // Scroll listener
    window.addEventListener("scroll", this.onScrollSticky, { passive: true });
  }

  calculateInitialOffsets() {
    if (!this.navigationElement) return;

    // Read sticky header height from CSS custom property
    this.headerHeight =
      parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-desktop-sticky-height")
          .replace(/px/gi, "")
      ) || 0;

    // Offsets for navigation (always used)
    const navigationRect = this.navigationElement.getBoundingClientRect();
    this.navigationOffsetTop = navigationRect.top + window.scrollY;

    // Offsets for items (only if present)
    if (this.itemsElement) {
      const itemsRect = this.itemsElement.getBoundingClientRect();
      this.itemsOffsetTop = itemsRect.top + window.scrollY;
      // Expose heights as CSS vars (used by CSS to stack both fixed bars on mobile)
      document.documentElement.style.setProperty(
        "--items-height",
        `${this.itemsElement.offsetHeight}px`
      );
    }

    document.documentElement.style.setProperty(
      "--navigation-height",
      `${this.navigationElement.offsetHeight}px`
    );

    // Run once to normalize state after recalculation
    this.onScrollSticky();
  }

  onScrollSticky() {
    if (!this.navigationElement) return;

    const scrollPosition = window.scrollY;
    const isMobile = window.innerWidth < 960;

    // ----- NAV: sticky on ALL breakpoints after crossing its top -----
    if (scrollPosition >= this.navigationOffsetTop - this.headerHeight) {
      if (!this.navigationElement.classList.contains("is-sticky")) {
        this.navigationElement.classList.add("is-sticky");

        // Create placeholder to avoid layout jump
        if (!this.navigationPlaceholder) {
          this.navigationPlaceholder = document.createElement("div");
          this.navigationPlaceholder.style.height = `${this.navigationElement.offsetHeight}px`;
          this.navigationElement.parentNode.insertBefore(
            this.navigationPlaceholder,
            this.navigationElement
          );
        }
      }
    } else {
      if (this.navigationElement.classList.contains("is-sticky")) {
        this.navigationElement.classList.remove("is-sticky");
        if (this.navigationPlaceholder) {
          this.navigationPlaceholder.parentNode.removeChild(
            this.navigationPlaceholder
          );
          this.navigationPlaceholder = null;
        }
      }
    }

    // ----- ITEMS: sticky ONLY on mobile -----
    if (this.itemsElement) {
      if (
        isMobile &&
        scrollPosition >= this.itemsOffsetTop - this.headerHeight
      ) {
        if (!this.itemsElement.classList.contains("is-sticky")) {
          this.itemsElement.classList.add("is-sticky");

          if (!this.itemsPlaceholder) {
            //this.itemsPlaceholder = document.createElement('div');
            //this.itemsPlaceholder.style.height = `${this.itemsElement.offsetHeight}px`;
            //this.itemsElement.parentNode.insertBefore(this.itemsPlaceholder, this.itemsElement);
          }
        }
      } else {
        if (this.itemsElement.classList.contains("is-sticky")) {
          this.itemsElement.classList.remove("is-sticky");
          if (this.itemsPlaceholder) {
            this.itemsPlaceholder.parentNode.removeChild(this.itemsPlaceholder);
            this.itemsPlaceholder = null;
          }
        }
        // Ensure items are reset on desktop
        if (!isMobile) {
          this.itemsElement.classList.remove("is-sticky");
        }
      }
    }
  }
}

if (!customElements.get("product-options-component")) {
  customElements.define("product-options-component", ProductOptionsComponent);
}

class ProductPackOption extends HTMLElement {
  constructor() {
    super();

    this.variantId = this.getAttribute("data-variant-id");
    this.productId = this.getAttribute("data-product-id");
    this.productTitle = this.getAttribute("data-product-title");
    this.variantTitle = this.getAttribute("data-variant-title");
    this.variantScentName = this.getAttribute("data-variant-scent-name");
    this.variantEdition = this.getAttribute("data-variant-edition") || "";
    this.variantPrice = parseFloat(
      this.getAttribute("data-variant-price").replace("$", "")
    );
    this.openModalBtn = this.querySelector(".product-pack-option__open-modal");

    if (this.openModalBtn) {
      const handle = this.getAttribute("data-product-handle") || "";
      if (handle) this.openModalBtn.dataset.productHandle = handle;
      if (this.variantId) this.openModalBtn.dataset.variantId = this.variantId;
    }

    const imgElement = this.querySelector(".product-pack-option__media img");
    this.variantImageAttrs = extractImageAttrsFromElement(imgElement);

    if (!this.variantImageAttrs) {
      console.error(
        "[BYP] Image not found or invalid in ProductPackOption:",
        this
      );
    }

    this.addButton = this.querySelector(".add-to-pack-button");
    //this.increaseButton = this.querySelector('.increase-quantity');
    //this.decreaseButton = this.querySelector('.decrease-quantity');
    this.quantityInput = this.querySelector(".product-pack-option__qty-value");

    this.quantity = this.quantityInput;

    this.initEvents();

    document.addEventListener("packStateChanged", (event) => {
      this.updateButtonState(event);
    });
  }

  initEvents() {
    this.addButton.addEventListener("click", () => this.addToPack());

    //this.increaseButton.addEventListener('click', () => this.increaseQuantity());
    //this.decreaseButton.addEventListener('click', () => this.decreaseQuantity());

    this.openProductModal = (triggerEl) => {
      const handle = this.getAttribute("data-product-handle");
      if (!handle) return;

      const modal = document.querySelector(`#modal-pack-product-${handle}`);
      if (!modal) return;

      const trigger = triggerEl || this.openModalBtn;
      if (!trigger) return;

      window.emit("modal:open", null, {
        modalContent: modal,
        trigger,
      });

      document.dispatchEvent(
        new CustomEvent("byp:modal:open", {
          detail: { trigger },
        })
      );
    };

    // 1) Eye click still works (same trigger/data as before)
    if (this.openModalBtn) {
      this.openModalBtn.addEventListener("click", (e) => {
        const variantBtn = e.target.closest(".product-pack-option__open-modal");
        if (!variantBtn) return;
        this.openProductModal(variantBtn);
      });
    }

    this.addEventListener("click", (e) => {
      const target = e.target;

      const isExcluded = target.closest(
        [
          ".add-to-pack-button",
          ".product-pack-option__qty",
          ".product-pack-option__qty-btn",
          "button",
          "input",
          "select",
          "textarea",
          "[data-action]",
        ].join(",")
      );

      if (isExcluded) return;

      if (!this.openModalBtn) return;

      this.openProductModal(this.openModalBtn);
    });
  }

  addToPack() {
    if (this.variantImageAttrs) {
      const item = {
        id: this.variantId,
        productId: this.productId,
        collectionId: "314985971899",
        title: this.variantTitle,
        productTitle: this.productTitle,
        scentName: this.variantScentName,
        price: this.variantPrice,
        imageAttrs: this.variantImageAttrs,
        quantity: this.quantity++,
        edition: this.variantEdition,
      };

      packState.addItem(item);

      this.setActive(true);
    }
  }

  increaseQuantity() {
    if (packState.isPackFull()) {
      // Show full pack modal
      const content = document.querySelector("#modal-pack-full");
      window.emit("modal:open", null, {
        modalContent: content,
      });

      return;
    }
    this.quantity++;
    this.quantityInput.value = this.quantity;

    // Update global quantity
    const item = {
      id: this.variantId,
      productId: this.productId,
      collectionId: "314985971899",
      title: this.variantTitle,
      productTitle: this.productTitle,
      scentName: this.variantScentName,
      price: this.variantPrice,
      imageAttrs: this.variantImageAttrs,
      quantity: 1,
      edition: this.variantEdition,
    };
    packState.addItem(item);
  }

  decreaseQuantity() {
    if (this.quantity > 1) {
      this.quantity--;
      this.quantityInput.value = this.quantity;

      const indexToRemove = packState.getLastIndexOfItem(this.variantId);
      if (indexToRemove !== -1) {
        packState.removeItemByIndex(indexToRemove);
      }
    } else if (this.quantity === 1) {
      const indexToRemove = packState.getLastIndexOfItem(this.variantId);
      if (indexToRemove !== -1) {
        packState.removeItemByIndex(indexToRemove);
        this.quantityInput.value = this.quantity;

        this.setActive(false);
      }
    }
  }

  setActive(isActive) {
    this.classList.toggle("is-active", isActive);
  }

  updateButtonState(event) {
    const isPackFull = packState.isPackFull();
    const isItemInPack = event.detail.selectedItems.some(
      (item) => item.id === this.variantId
    );
    const itemCountInPack = packState.countItemsByVariantId(this.variantId);

    if (isPackFull) {
      this.addButton.disabled = true;
      this.addButton.classList.add("disabled");
      if (isItemInPack) {
        this.setActive(true);
      }
    } else if (!isItemInPack) {
      this.setActive(false);
      this.addButton.disabled = false;
      this.addButton.classList.remove("disabled");
    } else {
      this.addButton.disabled = false;
      this.addButton.classList.remove("disabled");
      this.setActive(true);
    }

    this.quantity = itemCountInPack;
    this.quantityInput.value = this.quantity;
  }
}

customElements.define("product-pack-option", ProductPackOption);

/* =========================================================
   BYP — Simple Modal
   handles dynamic content replacement when modal opens
   Triggered by: document.dispatchEvent(new CustomEvent('byp:modal:open', { detail: { trigger } }))
   ========================================================= */
(() => {
  // Selectors
  const MODAL_SHELL = document.querySelector(".modal.popover[data-modal]");
  if (!MODAL_SHELL) return; // Stop if modal wrapper not found

  // Classes for selected/unselected states
  const SELECTED = ["selected", "tw:border-black", "tw:shadow-md"];
  const UNSELECTED = ["tw:border-transparent", "tw:shadow-none"];

  const TAB_ACTIVE = ["active", "tw:text-black", "tw:bg-light-grey"];
  const TAB_INACTIVE = ["tw:text-grey", "tw:bg-light-grey/30"];

  // get current product modal content
  const getInner = () =>
    MODAL_SHELL.querySelector('[data-byp-modal="product"]');

 function initBYPModal(trigger) {
    const inner = getInner();
    if (!inner) return;

    const seedId = trigger?.dataset?.variantId;

    const grid = inner.querySelector(".variant-grid-container");
    const tabs = inner.querySelectorAll(".collection-tab[data-collection]");
    const cards = inner.querySelectorAll(
      ".variant-grid-item[data-variant-type][data-variant-id]"
    );
    const sizeTabs = inner.querySelectorAll("[data-size-tabs] [data-size]");

    let selectedCard = null;
    let activeType = "";
    let activeSize = "";

    const btnATC = inner.querySelector(
      '.product-pack-modal__atc,[data-action="add-selected-to-bundle"]'
    );
    const qtyWrap = inner.querySelector(".product-pack-option__qty");
    const qtyInput = inner.querySelector(".product-pack-modal__qty-value");

    const ratingWidget = inner.querySelector('[data-bv-show="rating_summary"]');
    const BV_DEFAULT_COLOR = "#d2d2d2";

    if (!grid || !cards.length || !btnATC) return;

    // ------------------------------
    // BV helpers (unchanged patterns)
    // ------------------------------
    function waitForElement(selector, root = ratingWidget || inner || document) {
      return new Promise((resolve) => {
        const scope = root || document;
        const existing = scope.querySelector(selector);
        if (existing) return resolve(existing);

        const targetNode = scope === document ? document.documentElement : scope;
        const observer = new MutationObserver(() => {
          const found = scope.querySelector(selector);
          if (found) {
            observer.disconnect();
            resolve(found);
          }
        });

        observer.observe(targetNode, { childList: true, subtree: true });
      });
    }

    function getFallbackStarColor() {
      const cssVar = getComputedStyle(document.documentElement).getPropertyValue(
        "--color-products-rating-star"
      );
      const cleaned = (cssVar || "").trim();
      return cleaned || BV_DEFAULT_COLOR;
    }

    async function updateBVStarsColor(newColor) {
      if (!ratingWidget) return;
      const bvContainer = await waitForElement(".bv_main_container");
      if (!bvContainer) return;
      const reviewStarsEls = bvContainer.querySelectorAll('[offset="0%"]');
      reviewStarsEls.forEach((star) => {
        star.setAttribute("style", `stop-color: ${newColor}; stop-opacity: 1;`);
      });
    }

    async function adjustBVSummaryStyles(bvContainer) {
      if (!ratingWidget) return;
      const container = bvContainer || (await waitForElement(".bv_main_container"));
      if (!container) return;

      const starSvgEls = container.querySelectorAll(
        ".bv_stars_component_container svg"
      );
      if (starSvgEls.length) {
        starSvgEls.forEach((star, index) => {
          star.setAttribute("width", "16px");
          star.setAttribute("height", "15px");
          const isLast = index === starSvgEls.length - 1;
          star.style.marginRight = isLast ? "0px" : "6px";
        });
      }

      const bvStarsComponentContainerEl = container.querySelector(
        ".bv_stars_component_container"
      );
      if (bvStarsComponentContainerEl) {
        bvStarsComponentContainerEl.style.paddingRight = "0px";
        bvStarsComponentContainerEl.style.display = "inline-flex";
        bvStarsComponentContainerEl.style.alignItems = "center";
      }

      const textEls = container.querySelectorAll(
        ".bv_avgRating_component_container, .bv_numReviews_text"
      );
      if (textEls.length) {
        textEls.forEach((textEl) => {
          textEl.setAttribute(
            "style",
            "font-family: 'BrandonGrotesque', sans-serif !important; font-weight: 450 !important; font-size: 12px !important; line-height: 130% !important; letter-spacing: 1.2px !important; text-transform: uppercase !important; color: var(--color-native-navy) !important;"
          );
        });
      }
    }

    async function applyBVStarStyles(color) {
      if (!ratingWidget) return;
      ratingWidget.style.visibility = "hidden";
      const targetColor = color || getFallbackStarColor();
      const bvContainer = await waitForElement(".bv_main_container");
      if (bvContainer) {
        await adjustBVSummaryStyles(bvContainer);
        await updateBVStarsColor(targetColor);
      }
      ratingWidget.style.visibility = "";
    }

    // ------------------------------
    // Pack limits
    // ------------------------------
    const maxSlots = Number(packState?.maxItems || 3);

    function getInPackTotal() {
      return Array.isArray(packState?.selectedItems) ? packState.selectedItems.length : 0;
    }

    function getRemainingSlots() {
      return Math.max(0, maxSlots - getInPackTotal());
    }

    function remainingForSelected() {
      if (!selectedCard) return 0;
      return getRemainingSlots();
    }

    // ------------------------------
    // Tab UI helpers
    // ------------------------------
    function setActiveTab(tabEl) {
      if (!tabs || tabs.length === 0) return;

      tabs.forEach((t) => {
        const isActive = t === tabEl;
        t.classList.toggle("active", isActive);

        t.classList.remove(...TAB_ACTIVE, ...TAB_INACTIVE);

        if (isActive) {
          t.classList.add(...TAB_ACTIVE);
          t.setAttribute("aria-pressed", "true");
        } else {
          t.classList.add(...TAB_INACTIVE);
          t.setAttribute("aria-pressed", "false");
        }
      });
    }

    function setActiveSizeTab(tabEl) {
      if (!sizeTabs || sizeTabs.length === 0) return;

      sizeTabs.forEach((t) => {
        const isActive = t === tabEl;
        t.classList.toggle("active", isActive);

        t.classList.remove(...TAB_ACTIVE, ...TAB_INACTIVE);

        if (isActive) {
          t.classList.add(...TAB_ACTIVE);
          t.setAttribute("aria-pressed", "true");
        } else {
          t.classList.add(...TAB_INACTIVE);
          t.setAttribute("aria-pressed", "false");
        }
      });
    }

    // ------------------------------
    // NO recursion guards (critical)
    // ------------------------------
    let isUpdatingVisibility = false;
    let isRecovering = false;

    function isCardSelectable(card) {
      return (
        card &&
        !card.classList.contains("is-sold-out") &&
        !card.hasAttribute("disabled")
      );
    }

    function cardMatchesCurrentFilters(card, { type = activeType, size = activeSize } = {}) {
      const hasTypeTabs = !!tabs && tabs.length > 0;
      const hasSizeTabs = !!sizeTabs && sizeTabs.length > 0;

      const cardType = card.dataset.variantType?.trim() || "";
      const cardSize = card.dataset.variantSize?.trim() || "";

      const matchesType = !hasTypeTabs || !type || cardType === type;
      const matchesSize = !hasSizeTabs || !size || cardSize === size;

      return matchesType && matchesSize;
    }

    function updateTabAvailability() {
      // Disable SIZE tabs that have no selectable cards under current type
      if (sizeTabs && sizeTabs.length > 0) {
        sizeTabs.forEach((t) => {
          const size = t.dataset.size?.trim() || "";
          if (!size) return;

          const hasAny = Array.from(cards).some((c) => {
            if (!isCardSelectable(c)) return false;
            return cardMatchesCurrentFilters(c, { type: activeType, size });
          });

          t.toggleAttribute("disabled", !hasAny);
          t.classList.toggle("is-disabled", !hasAny);
          t.setAttribute("aria-disabled", hasAny ? "false" : "true");
        });
      }

      // Disable TYPE tabs that have no selectable cards under current size
      if (tabs && tabs.length > 0) {
        tabs.forEach((t) => {
          const type = t.dataset.collection?.trim() || "";
          if (!type) return;

          const hasAny = Array.from(cards).some((c) => {
            if (!isCardSelectable(c)) return false;
            return cardMatchesCurrentFilters(c, { type, size: activeSize });
          });

          t.toggleAttribute("disabled", !hasAny);
          t.classList.toggle("is-disabled", !hasAny);
          t.setAttribute("aria-disabled", hasAny ? "false" : "true");
        });
      }
    }

    function recoverInvalidActiveTabs() {
      if (isRecovering) return false;
      isRecovering = true;

      let didChange = false;

      // Recover activeSize if disabled
      if (sizeTabs && sizeTabs.length > 0 && activeSize) {
        const activeEl = Array.from(sizeTabs).find(
          (t) => (t.dataset.size || "").trim() === activeSize
        );
        const activeDisabled = activeEl ? activeEl.hasAttribute("disabled") : false;

        if (!activeEl || activeDisabled) {
          const firstEnabled = Array.from(sizeTabs).find(
            (t) => !t.hasAttribute("disabled")
          );
          const nextSize = (firstEnabled?.dataset?.size || "").trim();
          if (nextSize && nextSize !== activeSize) {
            activeSize = nextSize;
            setActiveSizeTab(firstEnabled);
            didChange = true;
          }
        }
      }

      // Recover activeType if disabled
      if (tabs && tabs.length > 0 && activeType) {
        const activeEl = Array.from(tabs).find(
          (t) => (t.dataset.collection || "").trim() === activeType
        );
        const activeDisabled = activeEl ? activeEl.hasAttribute("disabled") : false;

        if (!activeEl || activeDisabled) {
          const firstEnabled = Array.from(tabs).find(
            (t) => !t.hasAttribute("disabled")
          );
          const nextType = (firstEnabled?.dataset?.collection || "").trim();
          if (nextType && nextType !== activeType) {
            activeType = nextType;
            setActiveTab(firstEnabled);
            didChange = true;
          }
        }
      }

      isRecovering = false;
      return didChange;
    }

    function syncQtyAndATC(forceMin) {
      const allowed = remainingForSelected();

      if (qtyInput) {
        let current = parseInt(qtyInput.value || "0", 10) || 0;
        if (forceMin && allowed > 0 && current === 0) current = 1;
        current = Math.min(current, allowed);
        qtyInput.value = String(current);
      }

      const qtyNum = parseInt(qtyInput?.value || "0", 10) || 0;
      const canAdd = !!selectedCard && allowed > 0 && qtyNum > 0;

      btnATC.disabled = !canAdd;
      btnATC.setAttribute("aria-disabled", btnATC.disabled ? "true" : "false");
    }

    function selectCard(card, scroll = false) {
      // Visual selection
      cards.forEach((c) => {
        c.classList.remove(...SELECTED);
        c.classList.add(...UNSELECTED);
      });
      card.classList.add(...SELECTED);
      card.classList.remove(...UNSELECTED);
      selectedCard = card;

      // Update labels
      const subtitleEl = inner.querySelector(".scent-selector__subtitle");
      const scentLabel = inner.querySelector("[data-selected-scent-label]");
      const keywordsEl = inner.querySelector(".variant-keywords, #variant-keywords");

      const scentName = card.dataset.variantScentName || card.dataset.optionValue || "";
      const keywords = card.dataset.variantKeywords || "";
      const newSku = card.dataset.variantSku || card.dataset.variantId || "";
      const swatchColor = card.dataset.variantSwatchColor || getFallbackStarColor();

      if (subtitleEl) subtitleEl.textContent = scentName;
      if (scentLabel) scentLabel.textContent = scentName;

      if (keywordsEl) {
        const firstTextNode = Array.from(keywordsEl.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length > 0
        );
        if (firstTextNode) {
          firstTextNode.textContent = keywords;
        } else {
          keywordsEl.insertBefore(
            document.createTextNode(keywords + " "),
            keywordsEl.firstChild
          );
        }
      }

      if (ratingWidget) {
        ratingWidget.setAttribute("data-bv-product-id", newSku);
        ratingWidget.removeAttribute("data-bv-ready");

        if (window.BV && typeof window.BV.reveal === "function") {
          window.BV.reveal();
        } else if (window.bvCallback) {
          window.bvCallback();
        }

        applyBVStarStyles(swatchColor);
      }

      if (scroll) card.scrollIntoView({ block: "nearest" });

      syncQtyAndATC(true);
    }

    function ensureValidSelection() {
      if (selectedCard && isCardSelectable(selectedCard) && cardMatchesCurrentFilters(selectedCard)) {
        return;
      }

      const next = Array.from(cards).find(
        (c) => isCardSelectable(c) && cardMatchesCurrentFilters(c)
      );
      if (next) selectCard(next);
    }

    function updateVisibility() {
      if (isUpdatingVisibility) return;
      isUpdatingVisibility = true;

      const hasTypeTabs = !!tabs && tabs.length > 0;
      const hasSizeTabs = !!sizeTabs && sizeTabs.length > 0;

      cards.forEach((card) => {
        const cardType = card.dataset.variantType?.trim() || "";
        const cardSize = card.dataset.variantSize?.trim() || "";

        const matchesType = !hasTypeTabs || !activeType || cardType === activeType;
        const matchesSize = !hasSizeTabs || !activeSize || cardSize === activeSize;

        card.classList.toggle("hidden", !(matchesType && matchesSize));
      });

      grid.scrollTop = 0;

      updateTabAvailability();

      // If active tabs became invalid after availability update, recover and rerun ONCE
      const changed = recoverInvalidActiveTabs();
      if (changed) {
        isUpdatingVisibility = false;
        updateVisibility();
        return;
      }

      ensureValidSelection();

      isUpdatingVisibility = false;
    }

    function applyFilter(type) {
      if (!tabs || tabs.length === 0) {
        activeType = "";
        updateVisibility();
        return;
      }
      if (!type || type === activeType) return;

      activeType = type;
      updateVisibility();
    }

    function applySizeFilter(size) {
      if (!sizeTabs || sizeTabs.length === 0) {
        activeSize = "";
        updateVisibility();
        return;
      }
      if (!size || size === activeSize) return;

      activeSize = size;
      updateVisibility();
    }

    function adjustQty(delta) {
      if (!qtyInput) return;
      const allowed = remainingForSelected();
      const current = parseInt(qtyInput.value || "0", 10) || 0;
      const next = Math.min(Math.max(current + delta, 0), allowed);
      qtyInput.value = String(next);
      syncQtyAndATC(false);
    }

    function selectFirstVisible() {
      const first = Array.from(cards).find(
        (c) => !c.classList.contains("hidden") && isCardSelectable(c)
      );
      if (first) selectCard(first);
    }

    function seedInitialSelection() {
      const cardList = Array.from(cards);

      let card =
        cardList.find((c) => c.dataset.variantId === seedId) ||
        cardList.find((c) => c.classList.contains("selected")) ||
        cardList.find((c) => isCardSelectable(c));

      if (!card) {
        selectFirstVisible();
        return;
      }

      const type = card.dataset.variantType?.trim() || "";
      const size = card.dataset.variantSize?.trim() || "";

      if (tabs && tabs.length > 0 && type) {
        activeType = type;
        const tab = inner.querySelector(
          `.collection-tab[data-collection="${CSS.escape(type)}"]`
        );
        if (tab) setActiveTab(tab);
      }

      if (sizeTabs && sizeTabs.length > 0 && size) {
        activeSize = size;
        const sizeTab = inner.querySelector(
          `[data-size-tabs] [data-size="${CSS.escape(size)}"]`
        );
        if (sizeTab) setActiveSizeTab(sizeTab);
      }

      updateVisibility();

      if (card.classList.contains("hidden") || !isCardSelectable(card)) {
        selectFirstVisible();
        return;
      }

      requestAnimationFrame(() => {
        selectCard(card, true);
      });
    }

    // ------------------------------
    // Event binding (with disabled guards)
    // ------------------------------
    if (tabs && tabs.length > 0) {
      tabs.forEach((tab) => {
        tab.addEventListener("click", (e) => {
          e.preventDefault();
          if (tab.hasAttribute("disabled")) return;

          const type = tab.dataset.collection?.trim();
          if (!type) return;

          applyFilter(type);
          setActiveTab(tab);
        });
      });
    }

    if (sizeTabs && sizeTabs.length > 0) {
      sizeTabs.forEach((tab) => {
        tab.addEventListener("click", (e) => {
          e.preventDefault();
          if (tab.hasAttribute("disabled")) return;

          const size = tab.dataset.size?.trim();
          if (!size) return;

          applySizeFilter(size);
          setActiveSizeTab(tab);
        });
      });
    }

    grid.addEventListener("click", (e) => {
      const card = e.target.closest(".variant-grid-item");
      if (!card) return;
      if (card.classList.contains("hidden") || !isCardSelectable(card)) return;
      selectCard(card, true);
    });

    if (qtyWrap) {
      qtyWrap.querySelector(".decrease-quantity")?.addEventListener("click", (e) => {
        e.preventDefault();
        adjustQty(-1);
      });
      qtyWrap.querySelector(".increase-quantity")?.addEventListener("click", (e) => {
        e.preventDefault();
        adjustQty(+1);
      });
    }

    const onPackStateChanged = () => syncQtyAndATC(false);
    document.addEventListener("packStateChanged", onPackStateChanged);

    btnATC.addEventListener("click", (e) => {
      e.preventDefault();
      if (btnATC.disabled || !selectedCard) return;

      const variantId = selectedCard.dataset.variantId;
      const productId = selectedCard.dataset.productId || "";
      const productTitle =
        inner.querySelector(".product__title")?.textContent?.trim() || "";
      const scentName =
        selectedCard.dataset.variantScentName || selectedCard.dataset.optionValue || "";
      const imgEl = selectedCard.querySelector("img");

      const priceAttr = selectedCard.dataset.variantPrice || "";
      const priceFromBtn = (btnATC.textContent || "")
        .replace(/[^0-9.,]/g, "")
        .replace(",", ".");
      const price = parseFloat(priceAttr) || parseFloat(priceFromBtn) || 0;

      const qty = parseInt(qtyInput?.value || "1", 10) || 1;
      const edition = selectedCard.dataset.variantEdition || "";
      const sku = selectedCard.dataset.variantSku || "";
      const vendor = "";
      const productType = "";

      addVariantToPack({
        variantId,
        productId,
        productTitle,
        scentName,
        price,
        sku,
        vendor,
        productType,
        imageEl: imgEl,
        qty,
        edition,
      });

      MODAL_SHELL.querySelector("[data-modal-close], .modal__close-icon")?.click();
    });

    // ------------------------------
    // Seed initial state
    // ------------------------------
    seedInitialSelection();
    syncQtyAndATC(true);
}



  // Listen once; re-run each time modal opens
  document.addEventListener("byp:modal:open", (evt) => {
    const trigger = evt?.detail?.trigger;
    if (!trigger) return;
    initBYPModal(trigger);
  });
})();

const BUNDLE_CONTAINER = {
  productId: "8969744253102", // <-- REAL bundle product id (Shopify)
  variantId: "47367842889902", // <-- REAL bundle variant id (Shopify)
  handle: "dynamic-price-bundles-overhaul", // <-- REAL bundle handle (collection page handle)
};

let BUNDLE_COLLECTION_ID = null;

var BYP_BUNDLE_DATA = null;

async function initRechargeBundleData() {
  if (!window.recharge || !recharge.bundleData || !recharge.init) {
    console.error("[BYP] Recharge SDK not available.");
    return;
  }

  try {
    if (!recharge._bypInitialized) {
      await recharge.init({
        storeIdentifier: window.Shopify?.shop || "{{ shop.permanent_domain }}",
        appName: "Native BYP Dynamic Bundle",
        appVersion: "1.0.0",
      });
      recharge._bypInitialized = true;
    }

    if (BYP_BUNDLE_DATA) return BYP_BUNDLE_DATA;

    BYP_BUNDLE_DATA = await recharge.bundleData.loadBundleData(
      String(BUNDLE_CONTAINER.productId)
    );

    // --- Resolve bundle content collection ID from bundleData.collections ---
    if (BYP_BUNDLE_DATA && BYP_BUNDLE_DATA.collections) {
      const entries = Object.entries(BYP_BUNDLE_DATA.collections);

      if (entries.length === 1) {
        // Simplest case: bundle is configured with a single content collection.
        // Use that collection ID for all _rc_bundle_collection_id values.
        BUNDLE_COLLECTION_ID = entries[0][0];
      } else if (entries.length > 1) {
        // If there are multiple collections, pick the first one for now.
        // If needed, we can refine this later by matching on handle or metainfo.
        BUNDLE_COLLECTION_ID = entries[0][0];
      }

      console.log(
        "[BYP] Bundle collection id resolved from Recharge bundleData:",
        BUNDLE_COLLECTION_ID,
        BYP_BUNDLE_DATA.collections[BUNDLE_COLLECTION_ID]
      );
    } else {
      console.warn(
        "[BYP] bundleData.collections is empty, _rc_bundle_collection_id will not be set."
      );
    }
    // --- end resolve BUNDLE_COLLECTION_ID ---

    document.dispatchEvent(
      new CustomEvent("byp:bundleDataLoaded", {
        detail: { bundleData: BYP_BUNDLE_DATA },
      })
    );

    return BYP_BUNDLE_DATA;
  } catch (err) {
    console.error("[BYP] Failed to load Recharge bundle data:", err);
    return null;
  }
}

initRechargeBundleData();

function findBundleSellingPlan(bundleData, bundlePlanId) {
  if (!bundleData || !Array.isArray(bundleData.selling_plan_groups))
    return null;
  const idStr = String(bundlePlanId);

  for (const group of bundleData.selling_plan_groups) {
    if (!Array.isArray(group.selling_plans)) continue;
    const match = group.selling_plans.find((sp) => String(sp.id) === idStr);
    if (match) return match;
  }

  return null;
}

/**
 * For a given Shopify product (child of the bundle) find the selling plan
 * that "matches" the bundle selling plan.
 *
 * Simplified rule:
 * - Get all selling plans for that child product from bundleData.collections[*].products[*].
 * - Try to find one with the same "name" as the bundle plan
 *   (e.g. "3 month subscription with 25% discount").
 * - If no exact name match, fall back to the first available plan.
 */
function findProductSellingPlanForBundlePlan(
  bundleData,
  productId,
  bundlePlan
) {
  if (!bundleData || !bundlePlan || !bundleData.collections) return null;
  const pid = String(productId);

  let product = null;

  // Look through all collections to find this product
  for (const colKey in bundleData.collections) {
    const collection = bundleData.collections[colKey];
    if (!collection || !Array.isArray(collection.products)) continue;

    const found = collection.products.find((p) => String(p.id) === pid);
    if (found) {
      product = found;
      break;
    }
  }

  if (!product || !Array.isArray(product.selling_plan_groups)) {
    return null;
  }

  // Collect all selling plans defined for this child product
  const childPlans = [];
  product.selling_plan_groups.forEach((group) => {
    if (Array.isArray(group.selling_plans)) {
      childPlans.push(...group.selling_plans);
    }
  });

  if (!childPlans.length) return null;

  // Try to match by bundle plan options (e.g., frequency)
  const targetValues = new Set();
  if (Array.isArray(bundlePlan.options)) {
    bundlePlan.options.forEach((opt) => {
      const val = String(opt?.value || "").toLowerCase();
      const name = String(opt?.name || "").toLowerCase();
      if (val) targetValues.add(val);
      if (name && val) targetValues.add(`${name}:${val}`);
    });
  }

  // Extract target frequency number (e.g., "1-month" -> 1)
  let targetFrequencyNum = "";
  const freqFromOptions = Array.isArray(bundlePlan.options)
    ? bundlePlan.options
        .map((opt) => String(opt?.value || ""))
        .find((v) => /\d+/.test(v))
    : "";
  const nameFreqMatch =
    typeof bundlePlan.name === "string"
      ? bundlePlan.name.match(/(\d+)\s*month/i)
      : null;
  if (nameFreqMatch && nameFreqMatch[1]) {
    targetFrequencyNum = nameFreqMatch[1];
  } else if (freqFromOptions) {
    const freqNumMatch = String(freqFromOptions).match(/(\d+)/);
    if (freqNumMatch && freqNumMatch[1]) {
      targetFrequencyNum = freqNumMatch[1];
    }
  }

  const matchByOptions = childPlans.find((cp) => {
    const opts = Array.isArray(cp.options) ? cp.options : [];
    return opts.some((opt) => {
      const val = String(opt?.value || "").toLowerCase();
      const name = String(opt?.name || "").toLowerCase();
      if (!val) return false;
      return (
        targetValues.has(val) || (name && targetValues.has(`${name}:${val}`))
      );
    });
  });
  if (matchByOptions) return matchByOptions;

  if (targetFrequencyNum) {
    const byFreqNumber = childPlans.find((cp) => {
      const name = String(cp.name || "").toLowerCase();
      const numMatch = name.match(/(\d+)\s*month/);
      if (numMatch && numMatch[1]) {
        return numMatch[1] === targetFrequencyNum;
      }
      const opts = Array.isArray(cp.options) ? cp.options : [];
      return opts.some((opt) => {
        const v = String(opt?.value || "");
        const m = v.match(/(\d+)/);
        return m && m[1] === targetFrequencyNum;
      });
    });
    if (byFreqNumber) return byFreqNumber;
  }

  // Try exact name match
  const targetName = bundlePlan.name;
  if (targetName) {
    const byName = childPlans.find((cp) => cp.name === targetName);
    if (byName) return byName;
  }

  // Fallback: just use the first available plan for this product
  return childPlans[0] || null;
}

async function refreshCartAndEmit() {
  try {
    const response = await fetch(
      `${window.Shopify?.routes?.root || "/"}cart.js`,
      {
        headers: { Accept: "application/json" },
      }
    );
    const cartData = await response.json();

    window.emit("quick-cart:updated", cartData);
    window.emit("quick-cart:open", cartData);
    window.emit("cart:updated", cartData);

    return cartData;
  } catch (err) {
    console.error("[BYP] Failed to refresh cart:", err);
    return null;
  }
}

class PackPreview extends HTMLElement {
  constructor() {
    super();
    this.maxItems = 3;
    this.currentItems = [];
    this.suppressSnapshot = false;
    this.container = this.querySelector(".product-pack__contents-inner");
    this.packItemsList = this.querySelector("#pack-items-list");
    this.fullPackPrice = this.querySelector("#full-pack-price");
    this.discountedPackPrice = this.querySelector("#discounted-pack-price");
    this.fullPackPriceSubscription = this.querySelector(
      "#full-pack-price-subscription"
    );
    this.discountedPackPriceSubscription = this.querySelector(
      "#discounted-pack-price-subscription"
    );
    this.submitPack = this.querySelector("#submit-pack");
    this.packInput = this.querySelector("#pack-items");
    this.packPreviewCopyMessage = this.querySelector(
      ".product-pack__description"
    );

    this.subscription = {
      enabled: false,
      planId: null,
      plans: [],
    };

    this.subscriptionRoot = this.querySelector("#bundle-subscription");
    if (this.subscriptionRoot) {
      this.purchaseOptionRadios = this.subscriptionRoot.querySelectorAll(
        'input[name="purchase_option"]'
      );
      this.subscriptionOptionsWrapper = this.subscriptionRoot.querySelector(
        ".subscription__options"
      );
      this.sellingPlanDropdown = this.subscriptionRoot.querySelector(
        "#selling-plan-dropdown"
      );

      // New custom frequency selector elements
      this.frequencySelector = this.subscriptionRoot.querySelector(
        "[data-delivery-frequency]"
      );
      this.frequencyToggle = this.subscriptionRoot.querySelector(
        "[data-frequency-toggle]"
      );
      this.frequencyPanel = this.subscriptionRoot.querySelector(
        "[data-frequency-panel]"
      );
      this.frequencyOptionsList = this.subscriptionRoot.querySelector(
        "[data-frequency-options]"
      );
    } else {
      this.purchaseOptionRadios = null;
      this.subscriptionOptionsWrapper = null;
      this.sellingPlanDropdown = null;
      this.frequencySelector = null;
      this.frequencyToggle = null;
      this.frequencyPanel = null;
      this.frequencyOptionsList = null;
    }

    this.trigger = this.querySelector('[data-action="open-preview"]');
    this.onToggle = this.onToggle.bind(this);
    this.autoOpenOnAdd = this.autoOpenOnAdd.bind(this);

    this.init();
    this.submitPack.addEventListener("click", (event) =>
      this.handleSubmit(event)
    );

    if (this.trigger) this.trigger.addEventListener("click", this.onToggle);

    document.addEventListener("packStateChanged", this.autoOpenOnAdd, {
      passive: true,
    });
  }

  onToggle() {
    const isOpen = this.classList.toggle("is-open");
    if (this.trigger)
      this.trigger.setAttribute("aria-expanded", String(isOpen));
    // Prevent body scroll when open (mobile)
    if (window.innerWidth < 960) {
      document.documentElement.classList.toggle("byp-no-scroll", isOpen);
      document.body.classList.toggle("byp-no-scroll", isOpen);
    }
  }

  autoOpenOnAdd(evt) {
    if (window.innerWidth >= 960) return; // desktop unaffected
    if (this.classList.contains("is-open")) return; // already open
    const items = evt?.detail?.selectedItems || [];
    if (items.length > 0) this.onToggle();
  }

  /* recharge */

  toSelectionsFromCurrentItems() {
    // Step 1: aggregate items by variant
    const counts = new Map(); // key = variantId -> { productId, collectionId, qty }

    (this.currentItems || []).forEach((it) => {
      const vId = String(it.id);
      const cId = String(it.collectionId || "");
      const pId = String(it.productId || "");
      const q = parseInt(it.qty || it.quantity || 1, 10);
      if (!vId) return;

      const row = counts.get(vId) || {
        productId: pId,
        collectionId: cId,
        qty: 0,
      };
      row.qty += q;
      counts.set(vId, row);
    });

    const selections = [];
    const bundleData =
      typeof BYP_BUNDLE_DATA !== "undefined" ? BYP_BUNDLE_DATA : null;

    counts.forEach((row, variantId) => {
      const sel = {
        externalProductId: row.productId, // OK if empty; SDK can still build lines
        externalVariantId: variantId,
        quantity: row.qty,
      };

      // Only try to attach a selling plan when:
      // - subscription is enabled
      // - we have a selected bundle-level planId
      // - we already loaded bundleData from Recharge
      if (
        this.subscription?.enabled &&
        this.subscription?.planId &&
        bundleData &&
        row.productId
      ) {
        const bundlePlan = findBundleSellingPlan(
          bundleData,
          this.subscription.planId
        );

        if (bundlePlan) {
          const childPlan = findProductSellingPlanForBundlePlan(
            bundleData,
            row.productId,
            bundlePlan
          );

          if (childPlan) {
            // This is the critical piece: use the selling plan ID that belongs
            // to THIS product instead of reusing the bundle-level ID for all variants.
            sel.sellingPlan = childPlan.id;
          } else {
            console.warn(
              "[BYP] No matching child selling plan for product",
              row.productId,
              "and bundle plan",
              this.subscription.planId
            );
          }
        } else {
          console.warn(
            "[BYP] Unable to find bundle selling plan in bundleData for id",
            this.subscription.planId
          );
        }
      }

      selections.push(sel);
    });

    return selections;
  }

  /**
   * Build a JSON string of bundle component details from the current pack items.
   * Aggregates by variant ID so each component has the correct quantity.
   * Includes variantTitle so the Rebuy Smart Cart can display full variant names.
   */
  buildItemDetailsJSON() {
    const skuByVariantId = {};
    const categoryByVariantId = {};
    const vendorByVariantId = {};
    document.querySelectorAll("product-pack-option[data-variant-id]").forEach((el) => {
      const vid = el.dataset.variantId;
      if (vid) {
        skuByVariantId[vid] = el.dataset.variantSku || "";
        categoryByVariantId[vid] = el.dataset.variantCategory || "";
        vendorByVariantId[vid] = el.dataset.productVendor || "";
      }
    });

    const items = this.currentItems || [];
    const map = {};
    items.forEach((item) => {
      const key = String(item.id);
      if (!map[key]) {
        map[key] = {
          merchandiseId: `gid://shopify/ProductVariant/${key}`,
          variantId: key,
          productTitle: (item.productTitle || "").replace(/\s*\([^)]*\)\s*$/, ""),
          variantTitle: item.title || item.scentName || "",
          productType: item.productType || categoryByVariantId[key] || "",
          vendor: item.vendor || vendorByVariantId[key] || "",
          sku: item.sku || skuByVariantId[key] || "",
          quantity: 0,
          price: String(item.price || "0"),
        };
      }
      map[key].quantity += 1;
    });
    return JSON.stringify(Object.values(map));
  }

  /**
   * Store enriched bundle component data (with variant titles) in sessionStorage.
   * Keyed by sorted variant GIDs so the Rebuy cart script can look it up
   * from the _itemDetails data that the Cart Transform provides.
   */
  storeBundleComponentTitles(itemDetailsJSON) {
    try {
      const details = JSON.parse(itemDetailsJSON);
      const gidKey = details.map((d) => d.merchandiseId).sort().join("|");
      sessionStorage.setItem("byp_components_" + gidKey, itemDetailsJSON);
      // Store the cart display product handle (the Shopify bundle product shown after Cart Transform merge)
      // This is separate from BUNDLE_CONTAINER.handle which is the Recharge bundle product
      sessionStorage.setItem("byp_bundle_handle", "build-your-pack-1");
      console.log("[BYP] Stored bundle component titles in sessionStorage");
    } catch (err) {
      console.warn("[BYP] Failed to store bundle component titles:", err);
    }
  }

  /* ===== Build bundle payload for Recharge SDK (dynamic) ===== */
  buildDynamicBundlePayload() {
    return {
      externalVariantId: String(BUNDLE_CONTAINER.variantId),
      externalProductId: String(BUNDLE_CONTAINER.productId),
      selections: this.toSelectionsFromCurrentItems(),
    };
  }

  generateVisiblePackId() {
    if (this._visiblePackId) return this._visiblePackId;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datePart = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
      now.getDate()
    )}`;
    const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(
      now.getSeconds()
    )}`;

    // Simple incremental counter per session
    const globalKey = "BYP_PACK_COUNTER";
    const current = window[globalKey] || 0;
    const next = current + 1;
    window[globalKey] = next;

    this._visiblePackId = `byp-${datePart}-${timePart}-${next}`;
    return this._visiblePackId;
  }

  async addDynamicBundleToCart() {
    const bundle = this.buildDynamicBundlePayload();

    // Debug: inspect the bundle payload (can be removed later)
    console.log("[BYP] Dynamic bundle payload:", bundle);

    if (!bundle.selections || bundle.selections.length === 0) {
      console.warn("[BYP] No selections to submit.");
      alert("Please add products to your pack before adding it to cart.");
      return;
    }

    if (
      !window.recharge ||
      !recharge.bundle ||
      typeof recharge.bundle.getDynamicBundleItems !== "function"
    ) {
      console.error("[BYP] Recharge bundle SDK is not available on this page.");
      alert(
        "Subscription bundle is not available at the moment. Please try again later."
      );
      return;
    }

    try {
      // 1) Build cart lines for dynamic bundle via Recharge SDK
      const cartItems = await recharge.bundle.getDynamicBundleItems(
        bundle,
        BUNDLE_CONTAINER.handle
      );

      console.log("[BYP] Cart items returned by Recharge:", cartItems);

      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        console.error("[BYP] Recharge returned no cart items for this bundle.");
        alert(
          "We could not build your bundle. Please refresh the page and try again."
        );
        return;
      }

      // 2) Attach visible pack properties to each line item
      const packId = this.generateVisiblePackId();
      const packLabel = `Build Your Bundle ${packId}`;
      const itemDetailsJSON = this.buildItemDetailsJSON();

      cartItems.forEach((item) => {
        item.properties = item.properties || {};
        item.properties["Pack"] = packLabel; // visible label
        item.properties["_pack_id"] = packId; // visible technical ID

        if (BUNDLE_COLLECTION_ID) {
          item.properties["_rc_bundle_collection_id"] =
            String(BUNDLE_COLLECTION_ID);
        } else {
          console.warn(
            "[BYP] BUNDLE_COLLECTION_ID is not set. Bundle may not be recognized correctly in Recharge."
          );
        }
      });

      // 3) POST to Shopify cart and validate HTTP response
      const response = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart/add.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items: cartItems }),
        }
      );

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        // Not fatal: some themes do not rely on response body
        console.warn("[BYP] Could not parse cart/add.js JSON response:", e);
      }

      if (!response.ok) {
        console.error("[BYP] cart/add.js failed:", response.status, data);
        const msg =
          (data && data.description) ||
          (data && data.message) ||
          "Error adding bundle to cart.";
        alert(msg);
        return;
      }

      console.log("[BYP] Bundle successfully added to cart:", data);

      // 4) Store enriched component titles in sessionStorage for Rebuy to pick up
      this.storeBundleComponentTitles(itemDetailsJSON);

      // 5) Open quick cart and reset state
      await refreshCartAndEmit();
      this.resetPackState();
    } catch (err) {
      console.error("[Recharge dynamic bundle] Failed:", err);
      alert("Error adding bundle to cart. Check console for details.");
    }
  }

  addOneTimePackToCart() {
    if (!this.currentItems || !this.currentItems.length) return;

    const packId = this.generateVisiblePackId();
    const packLabel = `Build Your Bundle ${packId}`;
    const itemDetailsJSON = this.buildItemDetailsJSON();

    // Aggregate quantities per variant id
    const counts = this.currentItems.reduce((acc, item) => {
      const key = String(item.id);
      if (!acc[key]) acc[key] = 0;
      acc[key] += 1; // each item in currentItems is quantity 1
      return acc;
    }, {});

    const itemsToAdd = Object.keys(counts).map((variantId) => ({
      id: variantId,
      quantity: counts[variantId],
      properties: {
        // Visible label for the pack
        Pack: packLabel,
        // Technical pack id used by cart transform
        _pack_id: packId,
        // NOTE: No selling plan here on purpose (this is one-time flow)
        // NOTE: We do NOT set _rc_bundle_collection_id here so Recharge ignores these lines completely.
      },
    }));

    if (!itemsToAdd.length) return;

    // Store pending data so addItemsToCart can patch the bundle after Cart Transform runs
    this._pendingBundleDetails = { packId, itemDetailsJSON };

    // Use existing helper to add items to Shopify cart
    this.addItemsToCart(itemsToAdd);
  }

  handleSubmit(e) {
    e.preventDefault();

    if ((this.currentItems || []).length < this.maxItems) return;
    const selectedPurchaseOption =
      this.subscriptionRoot?.querySelector(
        'input[name="purchase_option"]:checked'
      )?.value || "onetime";

    if (selectedPurchaseOption === "subscribe") {
      // Subscription flow (Recharge bundle)
      if (!this.subscription?.enabled || !this.subscription?.planId) {
        // Optional: show a message or disable button until a plan is selected
        return;
      }

      // Use Recharge SDK to build subscription lines (with selling plans)
      this.addDynamicBundleToCart();
    } else {
      // One-time flow (pure Shopify, no Recharge dynamic items)
      this.addOneTimePackToCart();
    }
  }

  init() {
    document.addEventListener("packStateChanged", (event) => {
      this.currentItems = event.detail.selectedItems;
      this.updatePackUI();
    });

    this.packItemsList.addEventListener("click", (event) => {
      const removeButton = event.target.closest(".product-pack__item-remove");
      if (removeButton) {
        const itemIndex = removeButton.getAttribute("data-index");
        packState.removeItemByIndex(itemIndex);
      }
    });

    this.updatePackUI();

    this.initSubscriptionControls();

    // When bundle data is loaded from Recharge, populate the selling plan dropdown
    document.addEventListener("byp:bundleDataLoaded", (event) => {
      const bundleData = event.detail && event.detail.bundleData;
      if (bundleData) {
        this.populateSellingPlansFromBundleData(bundleData);
      }
    });

    // In case bundle data was already loaded before this component initialized
    if (typeof BYP_BUNDLE_DATA !== "undefined" && BYP_BUNDLE_DATA) {
      this.populateSellingPlansFromBundleData(BYP_BUNDLE_DATA);
    }
  }

  initSubscriptionControls() {
    if (
      !this.subscriptionRoot ||
      !this.purchaseOptionRadios ||
      !this.purchaseOptionRadios.length
    ) {
      // If the markup is not present, just skip wiring subscription logic.
      return;
    }

    // Initial UI state: hide subscription dropdown when "One-time" is selected.
    this.showSubscriptionSelector(false);

    // Listen for changes on the purchase option radios
    this.purchaseOptionRadios.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        const value = event.target.value;

        if (value === "subscribe") {
          // User selected "Subscribe & save"
          this.subscription.enabled = true;
          this.showSubscriptionSelector(true);
        } else {
          // User selected "One-time purchase"
          this.subscription.enabled = false;
          this.subscription.planId = null;

          if (this.sellingPlanDropdown) {
            this.sellingPlanDropdown.value = "";
          }

          // Clear visual selection in custom list
          if (this.frequencyOptionsList) {
            this.frequencyOptionsList
              .querySelectorAll("[data-frequency-option]")
              .forEach((el) => el.classList.remove("is-selected"));
          }

          this.showSubscriptionSelector(false);
        }
        this.updatePackPreviewCopy();
      });
    });

    // Listen for changes on the native selling plan dropdown (fallback / accessibility)
    if (this.sellingPlanDropdown) {
      this.sellingPlanDropdown.addEventListener("change", (event) => {
        const planId = event.target.value || null;
        this.subscription.planId = planId;

        // Keep custom list in sync if present
        if (this.frequencyOptionsList && planId) {
          this.frequencyOptionsList
            .querySelectorAll("[data-frequency-option]")
            .forEach((el) => {
              const isSelected = el.dataset.planId === planId;
              el.classList.toggle("is-selected", isSelected);
            });
        }
      });
    }

    if (this.frequencyToggle && this.frequencyPanel) {
      this.frequencyToggle.addEventListener("click", () => {
        const expanded =
          this.frequencyToggle.getAttribute("aria-expanded") === "true";
        const next = !expanded;
        this.frequencyToggle.setAttribute("aria-expanded", String(next));
        this.frequencyPanel.hidden = !next;
      });
    }

    if (this.frequencyOptionsList) {
      this.frequencyOptionsList.addEventListener("click", (event) => {
        const option = event.target.closest("[data-frequency-option]");
        if (!option) return;

        const planId = option.dataset.planId || "";
        const labelText =
          option.querySelector(".byp-frequency-text")?.textContent?.trim() ||
          "";

        // ----------------------------------------------------------
        // Update internal subscription state
        // ----------------------------------------------------------
        this.subscription.planId = planId || null;

        // ----------------------------------------------------------
        // Sync the visually hidden <select> (backing store)
        // ----------------------------------------------------------
        if (this.sellingPlanDropdown) {
          this.sellingPlanDropdown.value = planId;
          this.sellingPlanDropdown.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }

        // ----------------------------------------------------------
        // Update visual selection inside the custom list
        // ----------------------------------------------------------
        this.frequencyOptionsList
          .querySelectorAll("[data-frequency-option]")
          .forEach((el) => el.classList.toggle("is-selected", el === option));

        // ----------------------------------------------------------
        // IMPORTANT:
        // Set the toggle label to the selected frequency
        // ----------------------------------------------------------
        if (this.frequencySelector) {
          const headerLabel = this.frequencySelector.querySelector(
            ".byp-frequency-label"
          );
          if (headerLabel && labelText) {
            headerLabel.textContent = labelText;
          }
        }

        if (this.frequencyToggle && this.frequencyPanel) {
          this.frequencyToggle.setAttribute("aria-expanded", "false");
          this.frequencyPanel.hidden = true;
        }
      });
    }
  }

   /**
   * Returns true if any item in the current bundle is a Limited Edition variant.
   */
  hasLimitedEditionItem() {
    return (this.currentItems || []).some(
      (item) => item.edition === "Limited Edition"
    );
  }

  /**
   * Hides the "Subscribe & save" card when the bundle contains a Limited Edition
   * variant. The one-time purchase option stays visible. When no Limited Edition
   * items remain, the subscribe card is restored.
   */
  toggleSubscriptionForLimitedEdition() {
    if (!this.subscriptionRoot) return;

    const hasLimited = this.hasLimitedEditionItem();
    const subscribeCard = this.subscriptionRoot.querySelector(
      ".subscription__card:not(.subscription__card--onetime)"
    );

    if (hasLimited) {
      // Hide only the Subscribe & save card, keep one-time visible
      if (subscribeCard) subscribeCard.style.display = "none";
      this.subscription.enabled = false;
      this.subscription.planId = null;

      const onetimeRadio = this.subscriptionRoot.querySelector("#po-onetime");
      if (onetimeRadio) onetimeRadio.checked = true;

      this.showSubscriptionSelector(false);
    } else {
      // Restore the Subscribe & save card
      if (subscribeCard) subscribeCard.style.display = "";
    }
  }

  showSubscriptionSelector(visible) {
    if (!this.subscriptionOptionsWrapper || !this.sellingPlanDropdown) return;

    if (visible) {
      this.subscriptionOptionsWrapper.style.display = "";
      this.subscriptionOptionsWrapper.setAttribute("aria-hidden", "false");
      this.sellingPlanDropdown.disabled = false;
      return;
    }

    // Hide
    this.subscriptionOptionsWrapper.style.display = "none";
    this.subscriptionOptionsWrapper.setAttribute("aria-hidden", "true");
    this.sellingPlanDropdown.disabled = true;

    if (this.frequencyToggle) this.frequencyToggle.setAttribute("aria-expanded", "false");
    if (this.frequencyPanel) this.frequencyPanel.hidden = true;
  }

  populateSellingPlansFromBundleData(bundleData) {
    if (!bundleData || !Array.isArray(bundleData.selling_plan_groups)) return;
    const hasDropdown = !!this.sellingPlanDropdown;
    const hasCustomList = !!this.frequencyOptionsList;

    if (!hasDropdown && !hasCustomList) return;

    // Reset UI and internal list of plans
    if (hasDropdown) {
      this.sellingPlanDropdown.innerHTML = "";
    }
    if (hasCustomList) {
      this.frequencyOptionsList.innerHTML = "";
    }
    this.subscription.plans = [];

    const allPlans = [];

    bundleData.selling_plan_groups.forEach((group) => {
      if (!Array.isArray(group.selling_plans)) return;
      group.selling_plans.forEach((plan) => {
        allPlans.push(plan);
      });
    });

    if (!allPlans.length) {
      // No selling plans configured for this bundle; keep subscription UI hidden.
      this.showSubscriptionSelector(false);
      return;
    }

    this.subscription.plans = allPlans;

    // 1) Native <select> (backing store / accessibility)
    if (hasDropdown) {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Select a subscription option";
      placeholder.disabled = true;
      placeholder.selected = true;
      this.sellingPlanDropdown.appendChild(placeholder);

      allPlans.forEach((plan) => {
        const opt = document.createElement("option");
        opt.value = String(plan.id); // plan id is used as sellingPlanId
        opt.textContent = plan.name || `Plan #${plan.id}`;
        this.sellingPlanDropdown.appendChild(opt);
      });
    }

    
    if (hasCustomList) {
      allPlans.forEach((plan, index) => {
        const li = document.createElement("li");
        li.className = "byp-frequency-option";
        li.dataset.frequencyOption = "true";
        li.dataset.planId = String(plan.id);

        // Visible label (use plan.name from Recharge)
        const label = plan.name || `Delivered plan #${index + 1}`;

        li.innerHTML = `
            <span class="byp-frequency-checkmark" aria-hidden="true">✓</span>
            <span class="byp-frequency-text">${label}</span>
          `;

        this.frequencyOptionsList.appendChild(li);
      });
    }

    // ----------------------------------------------------------
    // Default selection: pick the 3rd option (index 2) if exists
    // ----------------------------------------------------------
    const defaultIndex = Math.min(2, allPlans.length - 1);
    const defaultPlan = allPlans[defaultIndex];
    const defaultPlanId = defaultPlan ? String(defaultPlan.id) : null;
    const defaultLabel = defaultPlan ? (defaultPlan.name || "") : "";

    if (defaultPlanId) {
      // Keep internal state ready (even if user hasn't chosen "subscribe" yet)
      this.subscription.planId = defaultPlanId;

      // 1) Backing <select>
      if (hasDropdown) {
        // Ensure placeholder isn't the selected one anymore
        const placeholderOpt = this.sellingPlanDropdown.querySelector('option[value=""]');
        if (placeholderOpt) placeholderOpt.selected = false;

        this.sellingPlanDropdown.value = defaultPlanId;
      }

      // 2) Custom list selection
      if (hasCustomList) {
        const options = Array.from(
          this.frequencyOptionsList.querySelectorAll("[data-frequency-option]")
        );

        options.forEach((el, idx) => el.classList.toggle("is-selected", idx === defaultIndex));
      }

      // 3) Update the header label + data-delivery-frequency attribute
      if (this.frequencySelector) {
        // This element is selected via: this.subscriptionRoot.querySelector("[data-delivery-frequency]")
        this.frequencySelector.dataset.deliveryFrequency = defaultLabel || defaultPlanId;

        const headerLabel = this.frequencySelector.querySelector(".byp-frequency-label");
        if (headerLabel && defaultLabel) headerLabel.textContent = defaultLabel;
      }
    }

  }

  /*handleSubmit(event) {
      event.preventDefault();

      if (packState.isPackFull()) {
        const packId = Math.floor(Math.random() * 1000000); // Generate random pack ID

        // Count the occurrences of each item.id in this.currentItems
        const itemCountMap = this.currentItems.reduce((acc, item) => {
          if (!acc[item.id]) {
            acc[item.id] = 0;
          }
          acc[item.id] += 1;
          return acc;
        }, {});

        const itemsToAdd = Object.keys(itemCountMap).map(itemId => ({
          id: itemId,
          quantity: itemCountMap[itemId],
          properties: {
            '_pack_id': packId,
          }
        }));

        this.addItemsToCart(itemsToAdd);
      }
    }*/

  async addItemsToCart(items) {
    const requestData = { items };

    try {
      // POST to Shopify cart
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestData),
      });

      // Parse JSON (not all themes use this response)
      let data = null;
      try {
        data = await response.json();
      } catch (err) {
        console.warn("[BYP] Could not parse cart/add.js JSON response:", err);
      }

      if (!response.ok) {
        console.error("[BYP] cart/add.js failed:", response.status, data);
        alert("Error adding items to cart. Please try again.");
        return;
      }

      // Capture bundle metadata and null _pendingBundleDetails in one pass
      let preBuiltItems = [];
      let packId = null;
      if (this._pendingBundleDetails) {
        const { packId: pid, itemDetailsJSON } = this._pendingBundleDetails;
        packId = pid || null;
        try {
          preBuiltItems = JSON.parse(itemDetailsJSON || "[]");
        } catch (e) {
          console.warn("[BYP] Could not parse itemDetailsJSON:", e);
        }
        this.storeBundleComponentTitles(itemDetailsJSON);
        this._pendingBundleDetails = null;
      }

      if (preBuiltItems.length) {
        const parentItem = data?.items?.[0] || {};
        document.dispatchEvent(new CustomEvent("byp:add-to-cart", {
          detail: {
            pack_id:        packId,
            product_id:     parentItem.product_id    || null,
            product_title:  parentItem.product_title || null,
            product_type:   parentItem.product_type  || null,
            product_vendor: parentItem.vendor        || null,
            variant_id:     parentItem.variant_id    || null,
            item_category:  parentItem.product_type  || null,
            value:          parentItem.final_price ? parentItem.final_price / 100 : 0,
            bundle_size:    preBuiltItems.length,
            items:          preBuiltItems,
          }
        }));
      }

      await refreshCartAndEmit();

      this.resetPackState();
    } catch (error) {
      console.error("[BYP] Error adding items to cart:", error);
      alert("Error adding items to cart. Please try again.");
    }
  }

  resetPackState() {
    this.suppressSnapshot = true;
    // Reset the state in PackState
    packState.selectedItems = [];

    // Notify that the state has changed
    packState.notifyStateChanged();
    // Clear cached pack id so a new bundle gets a fresh _pack_id
    this._visiblePackId = null;
    this.suppressSnapshot = false;
  }

  updatePackUI() {
    this.packItemsList.innerHTML = "";

    // Update current items
    this.currentItems.forEach((item, index) => {
      const itemElement = document.createElement("li");
      itemElement.classList.add("product-pack__item", "is-selected");

      const imageWrapper = document.createElement("div");
      imageWrapper.classList.add("product-pack__item-image-wrapper");

      const removeButton = document.createElement("button");
      removeButton.classList.add("product-pack__item-remove");
      removeButton.setAttribute("type", "button");
      removeButton.setAttribute("data-index", index);
      removeButton.setAttribute("aria-label", "Remove from pack");
      removeButton.innerHTML = `
          <svg class="product-pack__item-remove-icon" width="9" height="9" xmlns="http://www.w3.org/2000/svg">
            <path d="m1 1 7 7m0-7L1 8" stroke="#0a1f8f" stroke-width="1.5" fill="none"/>
          </svg>`;

      imageWrapper.appendChild(removeButton);

      const imageElement = document.createElement("img");
      imageElement.setAttribute("src", item.imageAttrs.src);
      imageElement.setAttribute("alt", item.imageAttrs.alt);
      imageElement.setAttribute("srcset", item.imageAttrs.srcset);
      imageElement.setAttribute("sizes", item.imageAttrs.sizes);
      imageElement.setAttribute("width", item.imageAttrs.width);
      imageElement.setAttribute("height", item.imageAttrs.height);

      imageElement.classList.add("product-pack__item-image");

      imageWrapper.appendChild(imageElement);

      const titleElement = document.createElement("h5");
      titleElement.classList.add(
        "product-pack__item-title",
        "tw:body-xs-regular"
      );
      titleElement.textContent = item.productTitle;

      const scentNameElement = document.createElement("h6");
      scentNameElement.classList.add("product-pack__item-scent");
      scentNameElement.textContent = item.scentName;

      itemElement.appendChild(imageWrapper);
      itemElement.appendChild(titleElement);
      itemElement.appendChild(scentNameElement);

      this.packItemsList.appendChild(itemElement);
    });

    for (let i = this.currentItems.length + 1; i <= this.maxItems; i++) {
      const placeholder = document.createElement("li");
      placeholder.classList.add("product-pack__item", "is-placeholder");
      placeholder.innerHTML = `
          <div class="product-pack__item-image-wrapper">
          </div>
        `;
      this.packItemsList.appendChild(placeholder);
    }

    // Update Pack Price
    this.fullPackPrice.textContent = `$${this.calculateFullPrice()}`;
    this.fullPackPriceSubscription.textContent = `$${this.calculateFullPrice()}`;
    this.discountedPackPrice.textContent = `$${this.calculateDiscountedPrice().toFixed(
      2
    )}`;
    this.discountedPackPriceSubscription.textContent = `$${this.calculateSubscriptionDiscountedPrice().toFixed(
      2
    )}`;

    // Hide Subscribe & Save when a Limited Edition variant is in the bundle
    this.toggleSubscriptionForLimitedEdition();
    this.updatePackPreviewCopy();
    this.updateSubmitButton();
    this.updateSpendBar();
  }

  updatePackPreviewCopy() {
    const itemCount = this.currentItems.length;
    const discountPct = this.subscription && this.subscription.enabled ? 25 : 20;

    switch (itemCount) {
      case 0:
        this.packPreviewCopyMessage.innerHTML =
          `Add 3 faves to get <span>${discountPct}% off.</span>`;
        break;
      case 1:
        this.packPreviewCopyMessage.innerHTML =
          `Great start! Find two more to <span>get ${discountPct}% off.</span>`;
        break;
      case 2:
        this.packPreviewCopyMessage.innerHTML =
          `Smelling good! Add one more to <span>get ${discountPct}% off.</span>`;
        break;
      case 3:
        this.packPreviewCopyMessage.innerHTML =
          `Cue the confetti — <span>you got ${discountPct}% off!</span>`;
        break;
    }
  }

  updateSubmitButton() {
    const itemCount = this.currentItems.length;
    const itemsLeft = this.maxItems - this.currentItems.length;

    this.container.setAttribute("data-items", itemCount);

    if (itemCount < 2) {
      this.container.classList.add("few-items");
    } else {
      this.container.classList.remove("few-items");
    }

    if (this.currentItems.length === this.maxItems) {
      this.container.classList.remove("resting");
      this.submitPack.disabled = false;
      this.submitPack.textContent = "Add to Cart";
      this.packInput.value = JSON.stringify(this.currentItems);

      this.showSubscriptionSelector(!!this.subscription?.enabled);
    } else {
      this.container.classList.add("resting");
      this.submitPack.disabled = true;
      this.submitPack.textContent = `ADD ANY ${itemsLeft} ${
        itemsLeft === 1 ? "MORE ITEM" : "ITEMS"
      }`;
      this.showSubscriptionSelector(false);
    }
  }

  updateSpendBar() {
    const spendBarItems = this.container.querySelectorAll(
      ".product-spend-bar__item"
    );
    const checkmark = this.container.querySelector(
      ".product-spend-bar__checkmark"
    );
    const itemCount = this.currentItems.length;

    spendBarItems.forEach((item, index) => {
      if (index < itemCount) {
        item.classList.add("fill");
      } else {
        item.classList.remove("fill");
      }
    });

    // Animate checkmark when bundle is complete
    if (checkmark) {
      if (itemCount === this.maxItems) {
        checkmark.classList.add("completed");
      } else {
        checkmark.classList.remove("completed");
      }
    }
  }

  calculateFullPrice() {
    return this.currentItems.reduce((total, item) => total + item.price, 0);
  }

  calculateDiscountedPrice() {
    return this.calculateFullPrice() * 0.8;
  }

  calculateSubscriptionDiscountedPrice() {
    return this.calculateFullPrice() * 0.75;
  }
}

customElements.define("pack-preview", PackPreview);

class PackState {
  constructor() {
    this.selectedItems = [];
    this.maxItems = 3;
  }

  addItem(item) {
    if (this.selectedItems.length < this.maxItems) {
      this.selectedItems.push(item);
      this.notifyStateChanged();
    }
  }

  countItemsByVariantId(variantId) {
    return this.selectedItems.filter((item) => item.id === variantId).length;
  }

  getLastIndexOfItem(variantId) {
    for (let i = this.selectedItems.length - 1; i >= 0; i--) {
      if (this.selectedItems[i].id === variantId) {
        return i;
      }
    }
    return -1;
  }

  removeItemByIndex(index) {
    this.selectedItems.splice(index, 1);
    this.notifyStateChanged();
  }

  notifyStateChanged() {
    const event = new CustomEvent("packStateChanged", {
      detail: {
        selectedItems: this.selectedItems,
        maxItems: this.maxItems,
      },
    });
    document.dispatchEvent(event);
  }

  isPackFull() {
    return this.selectedItems.length >= this.maxItems;
  }
}

const packState = new PackState();

class FilterOptionsComponent extends HTMLElement {
  constructor() {
    super();

    // ---- Existing config (kept) ----
    this.wrapperSelector = ".product-pack-options__wrapper";
    this.cardSelector = "product-pack-option";

    // Sidebar state: { [groupKey]: Set<string> }
    this.state = {};
    // Sidebar groups meta: [{ key, attr, el }]
    this.groupsMeta = [];

    // Bindings
    this.onChange = this.onChange.bind(this);
    this.applyFilters = this.applyFilters.bind(this);

    // Applied filters UI refs
    this._applied = { container: null, chips: null, clearAll: null };

    // Per-panel state (subcategory only; facets live in this.state)
    // Map<collectionHandle, { subcategory: '__all' | <handle> }>
    this.panelState = new Map();
  }

  /* =========================================================
     Lifecycle
     ========================================================= */
  connectedCallback() {
    // Discover sidebar groups from DOM
    this.groupsMeta = Array.from(
      this.querySelectorAll("[data-filter-group]")
    ).map((el) => ({
      key: el.getAttribute("data-filter-group"), // e.g., "scents"
      attr: el.getAttribute("data-attr"), // e.g., "variant-scent"
      multi: el.hasAttribute("data-multi"), // true when card attr is pipe-separated
      el,
    }));

    // Initialize sidebar state sets
    this.groupsMeta.forEach((g) => {
      this.state[g.key] = new Set();
    });

    // Observe product list mutations to rebuild sidebar options
    const wrapper = document.querySelector(this.wrapperSelector);
    if (wrapper) {
      new MutationObserver(() => this.rebuildFiltersFromDOM()).observe(
        wrapper,
        { childList: true, subtree: true }
      );
      this.rebuildFiltersFromDOM();
    }
    document.addEventListener("byp:products:rendered", () =>
      this.rebuildFiltersFromDOM()
    );

    // Subcategory chips per panel
    this.bindSubcategoryChips();

    // Applied filters UI
    this._applied.container = this.querySelector(".custom-sidebar-filter__header");
    this._applied.chips = this.querySelector("[data-sidebar-filter-tags]");
    this._applied.clearAll = this.querySelector("[data-sidebar-filter-clear]");
    this.bindAppliedFiltersUI();
    this.renderAppliedFilters();

    // Rebuild sidebar options + re-apply when the visible panel changes
    document.addEventListener("byp:layout:changed", () => {
      this.reconcileStateWithContext(); // drop stale sidebar selections for the new panel
      this.rebuildFiltersFromDOM();
      this.renderAppliedFilters();
    });
  }

  /* =========================================================
     Applied Filters — helpers for UI sync
     ========================================================= */

  /** Returns array of visible panel handles, based on [hidden] attribute. */
  getVisiblePanelHandles() {
    const panels = Array.from(
      document.querySelectorAll(
        ".product-pack-options__wrapper[data-collection-handle]"
      )
    );
    return panels
      .filter((p) => !p.hasAttribute("hidden"))
      .map((p) => p.dataset.collectionHandle);
  }

  /** Return the .product-pack-filters wrap for a given collection handle. */
  getChipsWrapByHandle(handle) {
    return document.querySelector(
      `.product-pack-options__heading[data-collection-handle="${CSS.escape(
        handle
      )}"] .product-pack-filters`
    );
  }

  /** Mark a subcategory chip as active by data-filter value (e.g., "__all", "limited-edition"). */
  setActiveChipByFilter(handle, filterVal) {
    const wrap = this.getChipsWrapByHandle(handle);
    if (!wrap) return;
    const chips = Array.from(
      wrap.querySelectorAll(".product-pack__navigation-link")
    );
    let target = chips.find(
      (c) => (c.dataset.filter || "").trim() === filterVal
    );
    if (!target && chips.length) target = chips[0]; // fallback
    chips.forEach((c) => {
      const active = c === target;
      c.classList.toggle("product-pack__navigation-link--active", active);
      c.setAttribute("aria-pressed", String(active));
    });
  }

  /** Query all sidebar inputs for a given group key (checkboxes, radios, selects). */
  queryGroupInputs(groupKey) {
    const groupEl = this.querySelector(
      `[data-filter-group="${CSS.escape(groupKey)}"]`
    );
    if (!groupEl) return [];
    const inputs = Array.from(
      groupEl.querySelectorAll(
        'input[type="checkbox"][data-filter], input[type="radio"][data-filter], select[data-filter]'
      )
    ).filter((el) => (el.getAttribute("data-filter") || "") === groupKey);
    return inputs;
  }

  /** Sync visual state of sidebar controls from this.state (checkbox/radio/select). */
  syncSidebarControlsFromState() {
    Object.entries(this.state).forEach(([groupKey, set]) => {
      const inputs = this.queryGroupInputs(groupKey);
      inputs.forEach((inp) => {
        if (inp.tagName === "SELECT") {
          const values = new Set(set);
          const isMultiple = inp.multiple === true;
          if (isMultiple) {
            Array.from(inp.options).forEach((opt) => {
              opt.selected = values.has(opt.value);
            });
          } else {
            const first = Array.from(values)[0] || "";
            inp.value = first;
          }
        } else if (inp.type === "checkbox" || inp.type === "radio") {
          inp.checked = set.has(inp.value);
        }
      });
    });
  }

  /** Reset all sidebar controls (uncheck and clear selects). */
  resetAllSidebarControls() {
    Object.keys(this.state).forEach((groupKey) => {
      const inputs = this.queryGroupInputs(groupKey);
      inputs.forEach((inp) => {
        if (inp.tagName === "SELECT") {
          const isMultiple = inp.multiple === true;
          if (isMultiple) {
            Array.from(inp.options).forEach((opt) => {
              opt.selected = false;
            });
          } else {
            const emptyOption = Array.from(inp.options).find(
              (o) => o.value === ""
            );
            inp.value = emptyOption
              ? ""
              : inp.options[0]
              ? inp.options[0].value
              : "";
          }
        } else if (inp.type === "checkbox" || inp.type === "radio") {
          inp.checked = false;
        }
      });
    });
  }

  /** Reset subcategory chips to "__all" for a list of panel handles. */
  resetSubcategoryChipsFor(handles) {
    handles.forEach((handle) => this.setActiveChipByFilter(handle, "__all"));
  }

  /** Wire events for chips list and "Clear all" button. */
  bindAppliedFiltersUI() {
    if (
      !this._applied.container ||
      !this._applied.chips ||
      !this._applied.clearAll
    )
      return;

    // Remove single chip (event delegation)
    this._applied.chips.addEventListener("click", (e) => {
      const btn = e.target.closest(".custom-sidebar-filter__chip");
      if (!btn) return;

      const type = btn.getAttribute("data-chip-type"); // 'facet' | 'subcategory'
      if (type === "facet") {
        const group = btn.getAttribute("data-chip-group");
        const value = btn.getAttribute("data-chip-value");
        if (group && value && this.state[group]) {
          // 1) Update internal state
          this.state[group].delete(value);
          // 2) Sync sidebar controls visually
          this.syncSidebarControlsFromState();
          // 3) Apply + re-render
          this.applyFilters();
          this.renderAppliedFilters();
        }
      } else if (type === "subcategory") {
        const handle = btn.getAttribute("data-chip-handle");
        if (handle) {
          // 1) Update state
          this.setSubcategory(handle, "__all");
          // 2) Sync chips visually (mark "__all")
          this.setActiveChipByFilter(handle, "__all");
          // 3) Apply + re-render
          this.applyFilters();
          this.renderAppliedFilters();
        }
      }
    });

    // Clear all (facets + subcategories)
    this._applied.clearAll.addEventListener("click", () => {
      // 1) Clear facet selections
      Object.keys(this.state).forEach((k) => this.state[k].clear());
      // 2) Reset all sidebar controls
      this.resetAllSidebarControls();
      // 3) Reset all subcategories to '__all' (sync only visible chips)
      const visibleHandles = this.getVisiblePanelHandles();
      this.resetSubcategoryChipsFor(visibleHandles);
      this.panelState.forEach((st) => {
        st.subcategory = "__all";
      });
      // 4) Apply + re-render
      this.applyFilters();
      this.renderAppliedFilters();
    });
  }

  /** Returns a human label for a collection handle (fallback to handle if no title is found). */
  formatCollectionLabel(handle) {
    const heading = document.querySelector(
      `.product-pack-options__heading[data-collection-handle="${CSS.escape(
        handle
      )}"]`
    );
    if (heading) {
      const t = heading.querySelector(
        "[data-collection-title], .product-pack-title, h2, h3"
      );
      if (t && t.textContent.trim()) return t.textContent.trim();
    }
    return this.formatLabel(handle);
  }

  /** Build chip HTML for a facet value. */
  chipHTMLForFacet(groupKey, value) {
    const label = `${this.formatLabel(groupKey)}: ${this.formatLabel(value)}`;
    return `
      <button type="button"
        class="custom-sidebar-filter__chip"
        role="listitem"
        aria-label="Remove ${this.formatLabel(value)} from ${this.formatLabel(
      groupKey
    )}"
        data-chip-type="facet"
        data-chip-group="${groupKey}"
        data-chip-value="${value}">
        <span class="custom-sidebar-filter__chip-text tw:label-medium">${label}</span>
        <span class="custom-sidebar-filter__chip-x" aria-hidden="true"><svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g opacity="0.5"><path d="M11.25 4.25L3.75 11.75" stroke="#0E1029" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M3.75 4.25L11.25 11.75" stroke="#0E1029" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></g></svg></span>
      </button>
    `;
  }

  /** Build chip HTML for subcategory (per panel handle). */
  chipHTMLForSubcategory(handle, subcat) {
    const colLabel = this.formatCollectionLabel(handle);
    const label = `${colLabel}: ${this.formatLabel(subcat)}`;
    return `
      <button type="button"
        class="custom-sidebar-filter__chip"
        role="listitem"
        aria-label="Remove subcategory ${this.formatLabel(
          subcat
        )} from ${colLabel}"
        data-chip-type="subcategory"
        data-chip-handle="${handle}">
        <span class="custom-sidebar-filter__chip-text">${label}</span>
        <span class="custom-sidebar-filter__chip-x" aria-hidden="true">×</span>
      </button>
    `;
  }

  /** Re-render the applied chips UI from current state. */
  renderAppliedFilters() {
    if (!this._applied.container || !this._applied.chips) return;

    const chips = [];

    // Facet chips from sidebar state
    Object.entries(this.state).forEach(([groupKey, set]) => {
      if (!set || set.size === 0) return;
      Array.from(set)
        .sort()
        .forEach((value) => {
          chips.push(this.chipHTMLForFacet(groupKey, value));
        });
    });

    // Subcategory chips for visible panels only (if not '__all')
    this.getVisiblePanelHandles().forEach((handle) => {
      const st = this.getPanelState(handle);
      if (st && st.subcategory && st.subcategory !== "__all") {
        chips.push(this.chipHTMLForSubcategory(handle, st.subcategory));
      }
    });

    this._applied.chips.innerHTML = chips.join("");
    const hasAny = chips.length > 0;
    this._applied.container.hidden = !hasAny;
    if (this._applied.clearAll) this._applied.clearAll.disabled = !hasAny;
  }

  /* =========================================================
     Sidebar helpers (existing)
     ========================================================= */
  getAllCards() {
    return Array.from(
      document.querySelectorAll(`${this.wrapperSelector} ${this.cardSelector}`)
    );
  }

  // Cards from panels that are currently visible (no [hidden] attribute on wrapper)
  getVisibleCards() {
    return Array.from(
      document.querySelectorAll(
        `.product-pack-options__wrapper:not([hidden]) ${this.cardSelector}`
      )
    );
  }

  collectFacets() {
    const out = {};
    this.groupsMeta.forEach((g) => (out[g.key] = new Set()));

    this.getVisibleCards().forEach((card) => {
      this.groupsMeta.forEach((g) => {
        if (!g.attr) return;
        const val = (card.getAttribute(`data-${g.attr}`) || "").trim();
        if (val) out[g.key].add(val);
      });
    });

    const sorted = {};
    Object.keys(out).forEach((k) => (sorted[k] = Array.from(out[k]).sort()));
    return sorted;
  }

  rebuildFiltersFromDOM() {
    const facets = this.collectFacets();
    this.groupsMeta.forEach((g) => this.renderGroup(g, facets[g.key] || []));
    this.applyFilters();
  }

  renderGroup(groupMeta, values) {
    const { key, el } = groupMeta;

    // Pre-rendered from Liquid (e.g. scent families) — just sync state and rebind
    if (el.dataset.bybPrefilled !== undefined) {
      el.querySelectorAll('input[type="checkbox"][data-filter]').forEach((inp) => {
        inp.checked = this.state[key].has(inp.value);
        inp.removeEventListener("change", this.onChange);
        inp.addEventListener("change", this.onChange);
      });
      return;
    }

    el.innerHTML = values.length
      ? `<ul class="custom-sidebar-filter__items">${values
          .map(
            (v) => `
          <li class="custom-sidebar-filter__item">
            <label class="custom-sidebar-filter__option">
              <input type="checkbox" class="custom-sidebar-filter__checkbox" value="${v}" data-filter="${key}" ${
              this.state[key].has(v) ? "checked" : ""
            } />
              <span class="custom-sidebar-filter__indicator"></span>
              <span class="custom-sidebar-filter__label tw:body-s tw:font-medium">${this.formatLabel(v)}</span>
            </label>
          </li>
        `
          )
          .join("")}</ul>`
      : "";

    el.querySelectorAll('input[type="checkbox"]').forEach((inp) =>
      inp.addEventListener("change", this.onChange)
    );
  }

  onChange(e) {
    const groupKey = e.currentTarget.getAttribute("data-filter");
    const value = e.currentTarget.value;
    if (e.currentTarget.checked) {
      this.state[groupKey].add(value);
    } else {
      this.state[groupKey].delete(value);
    }
    this.applyFilters();
  }

  /* =========================================================
     Subcategory chips per panel
     ========================================================= */
  // Ensure a panel state record exists and return it
  getPanelState(handle) {
    if (!this.panelState.has(handle)) {
      this.panelState.set(handle, { subcategory: "__all" });
    }
    return this.panelState.get(handle);
  }

  // Update only the subcategory for a given panel handle
  setSubcategory(handle, subcat) {
    const st = this.getPanelState(handle);
    st.subcategory = subcat && subcat.trim() ? subcat : "__all";
  }

  // Find the panel (collection) handle that owns a given card
  getCardHandle(card) {
    const wrapper = card.closest(
      ".product-pack-options__wrapper[data-collection-handle]"
    );
    return wrapper ? wrapper.dataset.collectionHandle : "";
  }

  // Parse pipe-separated subcategory handles from the card attribute
  getCardSubcats(card) {
    const raw = card.getAttribute("data-variant-subcategory") || "";
    return raw
      ? raw
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  }

  // Chip predicate: when subcategory == '__all' everything passes
  passesSubcategory(card, subcat) {
    if (!subcat || subcat === "__all") return true;
    const list = this.getCardSubcats(card);
    return list.includes(subcat);
  }

  // Wire chip clicks inside each panel heading; chips live in `.product-pack-filters`
  bindSubcategoryChips() {
    const headings = document.querySelectorAll(
      ".product-pack-options__heading[data-collection-handle]"
    );
    headings.forEach((heading) => {
      const handle = heading.dataset.collectionHandle;
      const wrap = heading.querySelector(".product-pack-filters");
      if (!wrap || wrap.__bypBound) return;
      wrap.__bypBound = true;

      const setActiveChip = (btn) => {
        const chips = Array.from(
          wrap.querySelectorAll(".product-pack__navigation-link")
        );
        chips.forEach((c) => {
          const active = c === btn;
          c.classList.toggle("product-pack__navigation-link--active", active);
          c.setAttribute("aria-pressed", String(active));
        });
      };

      wrap.addEventListener("click", (ev) => {
        const btn = ev.target.closest(".product-pack__navigation-link");
        if (!btn) return;
        ev.preventDefault();

        const subcat = (btn.dataset.filter || "__all").trim() || "__all";
        this.setSubcategory(handle, subcat);
        setActiveChip(btn); // local visual sync

        this.reconcileStateWithContext(); // drop sidebar selections with no products in this chip
        this.applyFilters();
      });

      // Seed default visual state ("All")
      const defaultBtn =
        wrap.querySelector(
          '.product-pack__navigation-link[data-filter="__all"]'
        ) || wrap.querySelector(".product-pack__navigation-link");
      if (defaultBtn) setActiveChip(defaultBtn);
    });
  }

  /* =========================================================
     Visibility (AND): sidebar facets ∧ subcategory chips
     ========================================================= */
  applyFilters() {
    const cards = this.getAllCards();

    cards.forEach((card) => {
      const matchesFacets = this.groupsMeta.every((g) =>
        !this.state[g.key].size || this.cardMatchesGroup(card, g, this.state[g.key])
      );
      const subcat = this.getPanelState(this.getCardHandle(card)).subcategory;

      // Keep .is-hidden so Load More continues to work
      card.classList.toggle("is-hidden", !(matchesFacets && this.passesSubcategory(card, subcat)));
    });

    // Notify others + refresh applied chips
    document.dispatchEvent(new CustomEvent("byp:filters:changed"));
    this.renderAppliedFilters();
    this.updateFilterAvailability();
  }

  /* =========================================================
     State reconciliation — drop sidebar selections that have no
     products in the current chip/panel context
     ========================================================= */
  reconcileStateWithContext() {
    const cards = this.getVisibleCards();

    this.groupsMeta.forEach((group) => {
      const toRemove = [...this.state[group.key]].filter((value) => {
        const optionSet = new Set([value]);
        return !cards.some(
          (card) =>
            this.passesSubcategory(card, this.getPanelState(this.getCardHandle(card)).subcategory) &&
            this.cardMatchesGroup(card, group, optionSet)
        );
      });

      toRemove.forEach((value) => {
        this.state[group.key].delete(value);
        const input = group.el.querySelector(
          `input[value="${CSS.escape(value)}"][data-filter="${CSS.escape(group.key)}"]`
        );
        if (input) input.checked = false;
      });
    });
  }

  /* =========================================================
     Filter availability — disable options that yield zero results
     ========================================================= */
  updateFilterAvailability() {
    const cards = this.getVisibleCards();

    this.groupsMeta.forEach((group) => {
      // Cards that satisfy every OTHER active group + the active subcategory chip
      const eligibleCards = cards.filter((card) => {
        const passesOtherGroups = this.groupsMeta.every((g) =>
          g.key === group.key || !this.state[g.key].size || this.cardMatchesGroup(card, g, this.state[g.key])
        );
        return passesOtherGroups && this.passesSubcategory(card, this.getPanelState(this.getCardHandle(card)).subcategory);
      });

      group.el
        .querySelectorAll('input[type="checkbox"][data-filter], input[type="radio"][data-filter]')
        .forEach((input) => {
          if (input.checked) {
            input.disabled = false;
            input.closest("li")?.classList.remove("is-filter-unavailable");
            return;
          }

          // Test this option in isolation — does it have any products in the current context?
          const optionSet = new Set([input.value]);
          const hasProducts = eligibleCards.some((card) => this.cardMatchesGroup(card, group, optionSet));

          input.disabled = !hasProducts;
          input.closest("li")?.classList.toggle("is-filter-unavailable", !hasProducts);
        });
    });
  }

  /* =========================================================
     Utilities
     ========================================================= */
  // Does a card's data attribute value match any entry in valueSet?
  cardMatchesGroup(card, group, valueSet) {
    const raw = (card.getAttribute(`data-${group.attr}`) || "").trim();
    if (group.multi) {
      return raw.split("|").map((s) => s.trim()).filter(Boolean).some((v) => valueSet.has(v));
    }
    return valueSet.has(raw);
  }

  formatLabel(s) {
    return s
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

if (!customElements.get("filter-options-component")) {
  customElements.define("filter-options-component", FilterOptionsComponent);
}

// --- Filters toggle button logic ---
const toggleBtn = document.getElementById("filtersToggle");
const filtersPanel = document.getElementById("filter-options-container");
const buildYourPack = document.getElementById("build-your-pack");

if (toggleBtn && filtersPanel) {
  toggleBtn.addEventListener("click", () => {
    const isOpen = toggleBtn.getAttribute("aria-expanded") === "true";
    toggleBtn.setAttribute("aria-expanded", !isOpen);
    toggleBtn.querySelector("span").textContent = isOpen
      ? "SHOW FILTERS"
      : "HIDE FILTERS";
    filtersPanel.classList.toggle("is-open", !isOpen);
    buildYourPack.classList.toggle("is-filters-open", !isOpen);
  });
}

const viewResultsBtn = document.querySelector(
  "#filter-options-container .filters__view-results"
);
if (viewResultsBtn && toggleBtn && filtersPanel && buildYourPack) {
  viewResultsBtn.addEventListener("click", () => {
    const isOpen = toggleBtn.getAttribute("aria-expanded") === "true";
    if (!isOpen) return; // already closed

    // Close using the exact same state used by the toggle
    toggleBtn.setAttribute("aria-expanded", "false");
    const span = toggleBtn.querySelector("span");
    if (span) span.textContent = "SHOW FILTERS";
    filtersPanel.classList.remove("is-open");
    buildYourPack.classList.remove("is-filters-open");
  });
}

/* Load More logic per panel: shows 3 initial rows, adds 2 per click */

(() => {
  // Selectors
  const PANEL_SEL = ".product-pack-options__wrapper[data-collection-handle]";
  const GRID_SEL = ".product-pack-options__row";
  const CARD_SEL = "product-pack-option";
  const BTN_WRAP = ".product-pack-options__loadmore[data-collection-handle]";
  const BTN_SEL = ".load-more-btn";

  // Config
  const INITIAL_ROWS = 3;
  const INCREMENT_ROWS = 2;

  // Internal state per panel: rows currently revealed
  const state = new Map(); // key = handle, value = { rowsShown: number }

  function panelKey(panelEl) {
    return panelEl?.dataset?.collectionHandle || "";
  }

  function getGrid(panelEl) {
    return panelEl.querySelector(GRID_SEL);
  }

  function getButton(panelEl) {
    const handle = panelKey(panelEl);
    const wrap = document.querySelector(
      `${BTN_WRAP}[data-collection-handle="${CSS.escape(handle)}"]`
    );
    return wrap ? wrap.querySelector(BTN_SEL) : null;
  }

  function getColumnCount(gridEl) {
    // Read computed grid-template-columns to stay in sync with CSS (2/3/4 cols)
    const gtc = getComputedStyle(gridEl).gridTemplateColumns || "";
    const count = gtc.split(" ").filter(Boolean).length;
    return Math.max(count, 1);
  }

  function getVisibleCards(panelEl) {
    // Only cards that are not filtered out
    const all = Array.from(panelEl.querySelectorAll(CARD_SEL));
    return all.filter((el) => !el.classList.contains("is-hidden"));
  }

  function applyCloak(cards, visibleCount) {
    // Show first N, cloak the rest
    cards.forEach((el, idx) => {
      const shouldCloak = idx >= visibleCount;
      el.classList.toggle("is-cloaked", shouldCloak);
    });
  }

  function updateButtonVisibility(panelEl, totalVisible, currentlyVisible) {
    const btn = getButton(panelEl);
    if (!btn) return;
    const nothingToShow = totalVisible <= currentlyVisible;
    const colTitle = (btn.dataset.collectionTitle || "").toUpperCase();
    const shouldHide = totalVisible === 0 || nothingToShow;
    btn.parentElement.toggleAttribute("hidden", shouldHide);
    btn.toggleAttribute("disabled", nothingToShow);
    btn.classList.toggle("is-disabled", nothingToShow);
    btn.textContent = `LOAD MORE ${colTitle}`;
  }

  function computeVisibleCount(panelEl, rows) {
    const grid = getGrid(panelEl);
    if (!grid) return 0;
    const cols = getColumnCount(grid);
    return rows * cols;
  }

  function revealRows(panelEl, rows) {
    const handle = panelKey(panelEl);
    if (!handle) return;

    const grid = getGrid(panelEl);
    if (!grid) return;

    const cards = getVisibleCards(panelEl); // already filtered list
    const desiredVisible = computeVisibleCount(panelEl, rows);
    const visibleCap = Math.min(cards.length, desiredVisible);

    applyCloak(cards, visibleCap);
    updateButtonVisibility(panelEl, cards.length, visibleCap);

    // Persist rowsShown for this panel
    state.set(handle, { rowsShown: rows });
  }

  function resetPanel(panelEl) {
    revealRows(panelEl, INITIAL_ROWS);
  }

  function onClickLoadMore(e) {
    const btn = e.currentTarget;
    const wrap = btn.closest(BTN_WRAP);
    const handle = wrap?.dataset?.collectionHandle || "";
    if (!handle) return;

    const panel = document.querySelector(
      `${PANEL_SEL}[data-collection-handle="${CSS.escape(handle)}"]`
    );
    if (!panel || panel.hasAttribute("hidden")) return;

    // Load all remaining variants by using Infinity (capped by cards.length in revealRows)
    revealRows(panel, Infinity);
  }

  function bindButtons() {
    document.querySelectorAll(BTN_WRAP).forEach((wrap) => {
      const btn = wrap.querySelector(BTN_SEL);
      if (!btn.__bypBound) {
        btn.addEventListener("click", onClickLoadMore);
        btn.__bypBound = true;
      }
    });
  }

  function refreshAllOpenPanels() {
    bindButtons();
    document.querySelectorAll(PANEL_SEL).forEach((panel) => {
      // Skip hidden panels (when a single collection is selected)
      if (panel.hasAttribute("hidden")) return;
      resetPanel(panel);
    });
  }

  // ---------- Event wiring ----------
  // Initial run (once DOM is ready)
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshAllOpenPanels);
  } else {
    refreshAllOpenPanels();
  }

  // When filters change, recompute visible list and reset to 3 rows
  document.addEventListener("byp:filters:changed", () => {
    refreshAllOpenPanels();
  });

  // When layout changes (e.g., open/close filters panel, change tab to single/all)
  document.addEventListener("byp:layout:changed", () => {
    refreshAllOpenPanels();
  });

  // On resize, column count may change -> recompute maintaining current rowsShown
  window.addEventListener("resize", () => {
    document.querySelectorAll(PANEL_SEL).forEach((panel) => {
      if (panel.hasAttribute("hidden")) return;
      const handle = panelKey(panel);
      const rows = state.get(handle)?.rowsShown ?? INITIAL_ROWS;
      revealRows(panel, rows);
    });
  });
})();

(function () {
  if (window.__bypInlineCartInit) return;
  window.__bypInlineCartInit = true;
  const RETRY_DELAYS = [100, 200, 400, 800, 1600, 2000];

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  // Subscription bundle grouping now handled globally in assets/theme.js (_renderSubscriptionBundles).
})();
